(() => {
  // Groups whose category band buckets by MATERIAL (Wood / Titanium / Leather)
  // instead of one tab per product. Add a group id here when a range is really
  // several products of the same material.
  const MATERIAL_CATEGORY_GROUPS = new Set(['vortex-sleeve']);

  class ProductBuilder {
    constructor(section) {
      this.section = section;
      this.configElement = section.querySelector('[data-product-builder-config]');
      this.previewElement = section.querySelector('[data-builder-preview]');
      this.controlsElement = section.querySelector('[data-builder-controls]');
      this.summaryElement = section.querySelector('[data-builder-summary]');
      this.totalElement = section.querySelector('[data-builder-total]');
      this.errorElement = section.querySelector('[data-builder-error]');
      this.successElement = section.querySelector('[data-builder-success]');
      this.addButton = section.querySelector('[data-builder-add]');
      this.randomizeButton = section.querySelector('[data-builder-randomize]');
      this.railElement = section.querySelector('[data-builder-rail]');
      this.trayHeadElement = section.querySelector('[data-builder-tray-head]');
      this.navToggleElement = section.querySelector('[data-builder-navtoggle]');

      this.config = this.readConfig();
      this.groups = this.buildGroups(this.config.options || []);
      this.selectedByGroup = {};
      this._catFilter = {};
      this._catScroll = {};
      this._stripFocus = {};
      this._offersOpen = false;
      this.isSubmitting = false;
      this.imageCache = new Map();
      // Stage layout (Benchmade-style: model on top, component rail, focused option tray).
      // Additive — all selection/pricing/cart logic is shared with the other modes.
      this.stageMode = this.config.stageMode === true;
      this.rowsMode = this.config.rowsMode === true;
      // Render options as per-component row sliders, independent of layout mode
      // (so inline mode can keep its baseline model/left column untouched and
      // just swap the right column to rows).
      this.optionRows = this.config.optionRows === true || this.rowsMode;
      // Thumbnails on the baseline stacked cards (no layout change).
      this.cardThumbs = this.config.cardThumbs === true;
      // Show each option's difference from its default option instead of the full price.
      this.relativePricing = this.config.relativePricing === true;
      this.modelHorizontal = this.config.modelHorizontal === true;
      this.navToggle = this.config.navToggle === true;
      this.navLabelFree = this.config.navLabelFree || 'Freestyle';
      this.navLabelGuided = this.config.navLabelGuided || 'Guided';
      // Remembered shopper choice wins over the section default.
      let storedNav = null;
      try { storedNav = window.localStorage.getItem('pb_nav_style'); } catch (e) { /* private mode */ }
      this.navStyle = (storedNav === 'pills' || storedNav === 'stepper')
        ? storedNav
        : (this.config.navStyle === 'stepper' ? 'stepper' : 'pills');
      this.focusedGroupId = this.groups[0] ? this.groups[0].id : null;

      section.__builder = this;
      this.init();
    }

    readConfig() {
      try {
        return JSON.parse(this.configElement.textContent);
      } catch (error) {
        console.error('Product builder config could not be parsed:', error);
        return {
          buildName: 'Custom Product',
          addButtonText: 'Add build to cart',
          successMessage: 'Build added to cart.',
          rootUrl: '/',
          baseImage: null,
          discountPct: 0,
          options: []
        };
      }
    }

    buildGroups(options) {
      const groups = [];

      options.forEach((option) => {
        if (!option.groupId || !option.optionId) return;

        let group = groups.find((item) => item.id === option.groupId);

        if (!group) {
          group = {
            id: option.groupId,
            label: option.groupLabel || option.groupId,
            summaryLabel: option.groupSummaryLabel || option.groupLabel || option.groupId,
            note: option.groupNote || '',
            required: Boolean(option.groupRequired),
            swatchLabels: Boolean(option.groupSwatchLabels),
            // Accessories (any NON-required group) are SINGLE-select, one per
            // CATEGORY. Each accessory group is exactly one category in the band
            // (sectionProducts collapses a multi-product group into one tab), so
            // one pick per group IS "one from each category". Picking a second
            // in the same category swaps it; picking the selected one clears it.
            // The ALL tab still shows every accessory — this bounds what can be
            // SELECTED, not what is visible. It also caps how many accessory
            // lines a cart can carry, which keeps us clear of the discount
            // function's instruction ceiling (see the 08-09 decision log).
            // Required components still honour the theme's "Allow multiple".
            allowMultiple: option.groupRequired
              ? Boolean(option.groupAllowMultiple)
              : false,
            maxSelections: Number(option.groupMaxSelections || 0),
            // Spread fans multi-selected layers apart (slot 1 = +100px, slot 2 =
            // -100px) so several SLEEVES read as separate pieces. Accessories each
            // have an authored position on the model — the stand under the body,
            // the station beside it — so fanning them puts the second one you pick
            // in the wrong place. Required groups keep it; accessories get 0.
            multiSpread: option.groupRequired ? Number(option.groupMultiSpread || 100) : 0,
            discountPct: null,
            creditCents: null,
            options: []
          };

          groups.push(group);
        }

        // Per-group display discount %, set on any one block in the group.
        if (group.discountPct === null && option.groupDiscountPct !== null && option.groupDiscountPct !== undefined) {
          group.discountPct = Number(option.groupDiscountPct);
        }

        // Per-group build credit (the sleeve's "one free with the build"), set
        // on any one block in the group, same as the discount %.
        if (group.creditCents === null && option.groupCreditCents !== null && option.groupCreditCents !== undefined) {
          group.creditCents = Number(option.groupCreditCents);
        }

        group.options.push({
          id: option.optionId,
          label: option.optionLabel || option.optionId,
          subtitle: option.metadata?.subtitle || '',
          variantId: this.normalizeVariantId(option.variantId),
          linkedVariantId: option.linkedVariantId ? Number(option.linkedVariantId) : null,
          priceCents: option.priceCents === null ? null : Number(option.priceCents || 0),
          discountPriceCents: (option.discountPriceCents === null || option.discountPriceCents === undefined) ? null : Number(option.discountPriceCents),
          layerImage: option.layerImage || null,
          optionImage: option.optionImage || null,
          productHandle: option.productHandle || null,
          productTitle: option.productTitle || null,
          productDescription: option.productDescription || null,
          optionInfo: option.optionInfo || null,
          optionHighlight: option.optionHighlight || null,
          giftTitle: option.giftTitle || null,
          giftImage: option.giftImage || null,
          giftPriceCents: (option.giftPriceCents === null || option.giftPriceCents === undefined) ? null : Number(option.giftPriceCents),
          available: option.available !== false,
          purchasable: option.purchasable === true,
          displayOnly: Boolean(option.displayOnly),
          selectedByDefault: Boolean(option.selectedByDefault),
          layerSettings: {
            x: Number(option.layerSettings?.x || 0),
            y: Number(option.layerSettings?.y || 0),
            scale: Number(option.layerSettings?.scale || 100) / 100,
            rotate: Number(option.layerSettings?.rotate || 0),
            zIndex: Number(option.layerSettings?.zIndex || 1)
          },
          adjust: this.parseAdjust(option),
          condImage: option.condImage || null,
          condWhen: (option.condWhen && String(option.condWhen).trim())
            ? String(option.condWhen).split(',').map((s) => s.trim().toLowerCase().replace(/\s+/g, '-')).filter(Boolean)
            : []
        });
      });

      return groups;
    }

    normalizeVariantId(value) {
      if (!value) return null;

      const stringValue = String(value).trim();
      const numericMatch = stringValue.match(/\d+$/);

      return numericMatch ? Number(numericMatch[0]) : null;
    }

    getOptionKey(groupId, optionId) {
      return `${groupId}::${optionId}`;
    }

    init() {
      // Full-page chrome hiding handled by the section liquid (layout_mode).
      this.setDefaultSelections();
      this.preloadInitialImages();
      this.render();

      this.addButton.addEventListener('click', () => {
        this.handleAddToCart();
      });

      if (this.randomizeButton) {
        this.randomizeButton.addEventListener('click', () => {
          this.handleRandomize();
        });
      }

      if (this.stageMode) {
        requestAnimationFrame(() => this.fitStage());
        window.setTimeout(() => this.fitStage(), 250);
        if (!ProductBuilder._stageResizeBound) {
          ProductBuilder._stageResizeBound = true;
          let t;
          const refit = () => {
            clearTimeout(t);
            t = window.setTimeout(() => {
              document.querySelectorAll('[data-product-builder-section]').forEach((s) => {
                if (s.__builder && s.__builder.stageMode) s.__builder.fitStage();
              });
            }, 120);
          };
          window.addEventListener('resize', refit);
          window.addEventListener('orientationchange', refit);
        }
      }

      // card_thumbs switches stacked (desktop) <-> row sliders (mobile); re-render
      // when we cross the breakpoint.
      if (this.cardThumbs) {
        this.setupSummaryToggle();
        const mq = window.matchMedia('(max-width: 820px)');
        const onBp = () => this.render();
        if (mq.addEventListener) mq.addEventListener('change', onBp);
        else if (mq.addListener) mq.addListener(onBp);
      }
    }

    // A show/hide toggle for the itemized "Your build" list; collapsed by default.
    // (Total + Randomize + Add stay visible.)
    setupSummaryToggle() {
      const card = this.section.querySelector('.product-builder__summary-card');
      if (!card || card.dataset.summaryToggle === 'true') return;
      card.dataset.summaryToggle = 'true';
      card.classList.add('product-builder__summary-card--collapsed');

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'product-builder__summary-toggle';
      const sync = () => {
        const collapsed = card.classList.contains('product-builder__summary-card--collapsed');
        toggle.textContent = collapsed ? 'Show build summary' : 'Hide build summary';
        toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      };
      toggle.addEventListener('click', () => {
        card.classList.toggle('product-builder__summary-card--collapsed');
        sync();
      });
      sync();
      card.insertBefore(toggle, card.firstChild);
    }

    preloadImage(src) {
      if (!src) return Promise.resolve(null);

      if (this.imageCache.has(src)) {
        return this.imageCache.get(src);
      }

      const promise = new Promise((resolve, reject) => {
        const image = new Image();

        image.onload = () => resolve(src);
        image.onerror = () => reject(new Error(`Image could not be loaded: ${src}`));
        image.src = src;
      });

      this.imageCache.set(src, promise);
      return promise;
    }

    async preloadCurrentSelection() {
      const promises = [];

      if (this.config.baseImage) {
        promises.push(this.preloadImage(this.config.baseImage).catch(() => null));
      }

      this.getSelectedOptions().forEach(({ option }) => {
        if (option.layerImage) {
          promises.push(this.preloadImage(option.layerImage).catch(() => null));
        }
      });

      await Promise.all(promises);
    }

    preloadInitialImages() {
      if (this.config.baseImage) {
        this.preloadImage(this.config.baseImage).catch(() => {});
      }

      this.getSelectedOptions().forEach(({ option }) => {
        if (option.layerImage) {
          this.preloadImage(option.layerImage).catch(() => {});
        }
      });

      window.setTimeout(() => {
        this.groups.forEach((group) => {
          group.options.forEach((option) => {
            if (option.layerImage) {
              this.preloadImage(option.layerImage).catch(() => {});
            }
            if (option.condImage) {
              this.preloadImage(option.condImage).catch(() => {});
            }
          });
        });
      }, 300);
    }

    // Back to the build the page loads with. Also drops the category filters and
    // any per-group model adjustments, or you'd reset the selections and still
    // be looking at whatever sub-category and nudge the last poke left behind.
    resetBuild() {
      this.setDefaultSelections();
      this._catFilter = {};
      this.adjustments = {};
      this.clearMessages();
      this.render();
    }

    setDefaultSelections() {
      this.groups.forEach((group) => {
        const pickable = (o) => this.config.hideSoldOut ? (o.purchasable || o.displayOnly) : o.available;
        const defaultOption = group.options.find((option) => option.selectedByDefault && pickable(option));
        const firstAvailableOption = group.options.find((option) => pickable(option));

        if (defaultOption) {
          this.selectedByGroup[group.id] = [defaultOption.id];
        } else if (group.required && firstAvailableOption) {
          this.selectedByGroup[group.id] = [firstAvailableOption.id];
        } else {
          this.selectedByGroup[group.id] = [];
        }
      });
    }

    // selectedByGroup holds an ARRAY of option ids per group (single-select groups cap at 1).
    getSelectedIds(group) {
      const v = this.selectedByGroup[group.id];
      return Array.isArray(v) ? v : (v ? [v] : []);
    }

    getSelectedOptionsForGroup(group) {
      return this.getSelectedIds(group)
        .map((id) => group.options.find((o) => o.id === id))
        .filter(Boolean);
    }

    isOptionSelectedAnywhere(optionId) {
      return this.groups.some((g) => this.getSelectedIds(g).includes(optionId));
    }

    // Fan-out offset for the Nth pick in a multi-select group: slot0=0, slot1=+spread (right),
    // slot2=-spread (left), slot3=+2*spread, slot4=-2*spread,...
    slotSpread(slotIndex, spread) {
      if (!slotIndex || !spread) return 0;
      const mag = Math.ceil(slotIndex / 2) * spread;
      return (slotIndex % 2 === 1) ? mag : -mag;
    }

    applyLinkedVariant(option) {
      if (!option.linkedVariantId) return;

      for (const g of this.groups) {
        const match = g.options.find((o) => o.variantId === option.linkedVariantId);
        if (match) {
          this.selectedByGroup[g.id] = match.id;
          break;
        }
      }
    }

    render() {
      this.renderPreview();
      this.renderAccessoryRail();
      if (this.stageMode) { this.renderNavToggle(); this.renderNav(); this.renderTrayHead(); }
      this.renderControls();
      this.renderSummary();
      this.renderTotal();
      this.updateAddButton();
      if (this.stageMode) requestAnimationFrame(() => this.centerTraySelection());
    }

    // Vertical rail down the left of the model: one thumbnail per selected
    // accessory (optional groups), so the build's add-ons stay visible while
    // the shopper is on another component's tab.
    renderAccessoryRail() {
      const col = this.section.querySelector('.product-builder__preview-column');
      if (!col) return;
      let rail = col.querySelector('.product-builder__acc-rail');

      const picks = [];
      const seenGift = new Set();
      this.groups.forEach((g) => {
        this.getSelectedOptionsForGroup(g).forEach((o) => {
          // Required COMPONENTS stay off the rail — they're already the model.
          // Their GIFTS do not: hydro's free WPA handle hangs off the chamber,
          // which is required, so skipping the whole group hid a freebie the
          // build always earns.
          if (!g.required) {
            const img = o.optionImage || o.layerImage;
            if (img) picks.push({ img: img, label: this.cleanLabel(o.label), group: g, option: o });
          }
          // A gift you've EARNED belongs on the rail too — it's part of the
          // build you're getting, even though it was never a selection. Keyed
          // by image so one gift shows once however many triggers earned it.
          if (o.giftImage && !seenGift.has(o.giftImage)) {
            seenGift.add(o.giftImage);
            picks.push({
              img: o.giftImage,
              label: this.cleanLabel(o.giftTitle || 'Free gift'),
              group: g, option: o, gift: true
            });
          }
        });
      });

      // Freebies sink to the BOTTOM of the rail, below what you actually picked.
      // WITHIN that block the NEWEST sits on top: earning a second gift should
      // draw the eye, and appending it under one that's been there a while does
      // the opposite. Order is remembered across renders — gifts still earned
      // hold their position, anything newly earned is prepended.
      const giftKeys = picks.filter((p) => p.gift).map((p) => p.img);
      const prevOrder = this._railGiftOrder || [];
      this._railGiftOrder = giftKeys.filter((k) => prevOrder.indexOf(k) === -1)
        .concat(prevOrder.filter((k) => giftKeys.indexOf(k) !== -1));
      const giftRank = this._railGiftOrder;
      // Array.sort is stable, so chosen accessories keep their group order.
      picks.sort((a, b) => {
        const ag = a.gift ? 1 : 0, bg = b.gift ? 1 : 0;
        if (ag !== bg) return ag - bg;
        if (!ag) return 0;
        return giftRank.indexOf(a.img) - giftRank.indexOf(b.img);
      });

      if (!picks.length) { if (rail) rail.remove(); return; }

      if (!rail) {
        rail = document.createElement('div');
        rail.className = 'product-builder__acc-rail';
        col.appendChild(rail);
      }
      // cheap diff: skip the rebuild when the same set is already shown
      const sig = picks.map((p) => (p.gift ? 'gift:' + p.img : p.option.id)).join('|');
      if (rail.dataset.sig === sig) return;
      rail.dataset.sig = sig;
      rail.innerHTML = '';

      picks.forEach((p) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'product-builder__acc-chip' + (p.gift ? ' product-builder__acc-chip--gift' : '');
        item.title = p.gift ? p.label + ' (free)' : p.label;
        item.setAttribute('aria-label', p.label);
        const im = document.createElement('img');
        im.src = p.img;
        im.alt = p.label;
        im.loading = 'lazy';
        item.appendChild(im);
        // Hover X removes the accessory straight from the rail, instead of
        // navigating to its tab to click it off. Gifts get none — they're earned
        // by another choice, so the only way to drop one is to drop its trigger.
        // A <span>, not a <button>: the chip is already a button and nesting one
        // inside another is invalid and swallows the inner click in some browsers.
        if (!p.gift) {
          const x = document.createElement('span');
          x.className = 'product-builder__acc-remove';
          x.textContent = '×';
          x.setAttribute('role', 'button');
          x.setAttribute('tabindex', '0');
          x.setAttribute('aria-label', 'Remove ' + p.label);
          const remove = (e) => {
            e.stopPropagation();   // don't also trigger the chip's jump-to-tab
            e.preventDefault();
            const ids = this.getSelectedIds(p.group).slice();
            const i = ids.indexOf(p.option.id);
            if (i === -1) return;
            // a required group must keep one pick; accessories can empty out
            if (p.group.required && ids.length === 1) return;
            ids.splice(i, 1);
            this.selectedByGroup[p.group.id] = ids;
            this.render();
          };
          x.addEventListener('click', remove);
          x.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') remove(e);
          });
          item.appendChild(x);
        }
        // tapping a chip jumps to that accessory's tab
        item.addEventListener('click', () => {
          // a gift has no tab of its own; jump to whatever earned it
          const sec = this.navSectionsCtabs().find((n) => n.groups.some((g) => g.id === p.group.id));
          if (!sec) return;
          this.focusedGroupId = sec.id;
          this._catFilter[sec.id] = p.option.productHandle || this._catFilter[sec.id];
          this.renderControls();
        });
        rail.appendChild(item);
      });
    }

    parseAdjust(option) {
      const raw = option.adjustGroups;
      if (!raw || !String(raw).trim()) return null;
      const groups = String(raw).split(',')
        .map((s) => s.trim().toLowerCase().replace(/\s+/g, '-'))
        .filter(Boolean);
      if (!groups.length) return null;
      return { groups, x: Number(option.adjustX || 0), y: Number(option.adjustY || 0) };
    }

    activeAdjustment(groupId) {
      let x = 0, y = 0;
      this.getSelectedOptions().forEach(({ option }) => {
        const adj = option.adjust;
        if (adj && adj.groups.indexOf(groupId) !== -1) { x += adj.x; y += adj.y; }
      });
      return { x, y };
    }

    renderPreview() {
      if (!this.previewElement.dataset.initialized) {
        this.previewElement.innerHTML = '';

        this.layerMap = new Map();

        if (this.config.baseImage) {
          const baseImage = document.createElement('img');
          baseImage.className = 'product-builder__preview-layer product-builder__preview-layer--base';
          baseImage.src = this.config.baseImage;
          baseImage.style.opacity = '1';
          this.previewElement.appendChild(baseImage);
        }

        this.groups.forEach((group) => {
          group.options.forEach((option) => {
            if (!option.layerImage) return;

            const img = document.createElement('img');
            img.className = 'product-builder__preview-layer';
            img.src = option.layerImage;
            img.style.opacity = '0';

            const layerSettings = option.layerSettings || {};

            // Offsets stored in px but rendered as a % of the preview reference width (720) so
            // they scale with the responsive model at every viewport size (with --builder-model-scale).
            img.style.setProperty('--layer-x', `${(Number(layerSettings.x) || 0) / 720 * 100}`);
            img.style.setProperty('--layer-y', `${(Number(layerSettings.y) || 0) / 720 * 100}`);
            img.style.setProperty('--layer-scale', layerSettings.scale || 1);
            img.style.setProperty('--layer-rot', Number(layerSettings.rotate) || 0);
            img.style.zIndex = layerSettings.zIndex || 1;

            const key = this.getOptionKey(group.id, option.id);
            this.layerMap.set(key, img);

            this.previewElement.appendChild(img);
          });
        });

        this.previewElement.dataset.initialized = 'true';
      }

      this.layerMap.forEach((img) => { img.style.opacity = '0'; });

      this.getSelectedOptions().forEach(({ group, option, slotIndex }) => {
        const img = this.layerMap.get(this.getOptionKey(group.id, option.id));
        if (!img) return;
        // Conditional image: when the listed options are ALSO selected, swap to condImage
        // (or hide this layer entirely if condImage is blank).
        let condSrc = option.layerImage;
        if (option.condWhen && option.condWhen.length && option.condWhen.every((id) => this.isOptionSelectedAnywhere(id))) {
          if (option.condImage) { condSrc = option.condImage; }
          else { return; }
        }
        if (condSrc && img.getAttribute('src') !== condSrc) img.setAttribute('src', condSrc);
        const ls = option.layerSettings || {};
        const adj = this.activeAdjustment(group.id);
        const spread = this.slotSpread(slotIndex, group.multiSpread || 0);
        img.style.setProperty('--layer-x', `${((Number(ls.x) || 0) + adj.x + spread) / 720 * 100}`);
        img.style.setProperty('--layer-y', `${((Number(ls.y) || 0) + adj.y) / 720 * 100}`);
        img.style.setProperty('--layer-rot', Number(ls.rotate) || 0);
        img.style.opacity = '1';
      });
    }

    cleanLabel(str) {
      // Strip a redundant leading "Optional " / "Add Optional " from a component
      // label. Optionality is conveyed by the "— optional" suffix instead, so the
      // titles read cleaner (e.g. "Optional Titanium Screens" -> "Titanium Screens").
      // Display-only; product/option names in cart and admin are untouched.
      return String(str || '').replace(/^\s*(?:add\s+optional|optional)\s+/i, '');
    }

    renderControls() {
      // Stage mode renders only the focused component's options as a scroll tray.
      // renderTray is idempotent, so selecting within a component updates in place
      // (no DOM wipe) and the scroll position is preserved.
      if (this.stageMode) { this.renderTray(); return; }
      // Option rows: one horizontal slider per component (works in any layout mode).
      if (this.optionRows) { this.renderRows(); return; }
      // card_thumbs: stacked cards on desktop, row sliders on mobile — and the
      // ctabs match design at every width.
      if (this.cardThumbs && (this.ctabsActive() || window.matchMedia('(max-width: 820px)').matches)) { this.renderRows(); return; }

      this.clearComponentTabs();
      this.controlsElement.innerHTML = '';
      delete this.controlsElement.dataset.builtMode;

      this.groups.forEach((group) => {
        const groupElement = document.createElement('div');
        groupElement.className = 'product-builder__group';
        groupElement.dataset.groupId = group.id;

        const heading = document.createElement('div');
        heading.className = 'product-builder__group-heading';

        const title = document.createElement('h3');
        title.className = 'product-builder__group-title';
        const titleText = this.cleanLabel(group.label);
        if (this.cardThumbs) {
          // One word per line — a vertical title beside the option cards.
          titleText.split(/\s+/).filter(Boolean).forEach((word) => {
            const w = document.createElement('span');
            w.className = 'product-builder__group-title-word';
            w.textContent = word;
            title.appendChild(w);
          });
        } else {
          title.textContent = titleText;
        }

        heading.appendChild(title);
        // Only badge required groups with a subtle "Required". Optional groups get
        // no badge at all (the absence of "Required" — plus a "No Selection" card —
        // signals optional); no "optional" wording anywhere.
        if (group.required) {
          const requirement = document.createElement('span');
          requirement.className = 'product-builder__group-requirement';
          requirement.textContent = 'Required';
          heading.appendChild(requirement);
        }
        if (this.showsMultiControl(group)) heading.appendChild(this.createMultiControl(group));
        groupElement.appendChild(heading);

        if (group.note) {
          const noteEl = document.createElement('div');
          noteEl.className = 'product-builder__group-note';
          noteEl.innerHTML = group.note;
          groupElement.appendChild(noteEl);
        }

        const optionsElement = document.createElement('div');
        optionsElement.className = 'product-builder__options';

        if (!group.required) {
          const noneButton = this.createOptionButton(group, null);
          optionsElement.appendChild(noneButton);
        }

        group.options.forEach((option) => {
          if (this.config.hideSoldOut && !option.purchasable && !option.displayOnly) return;
          const button = this.createOptionButton(group, option);
          optionsElement.appendChild(button);
        });

        groupElement.appendChild(optionsElement);
        this.controlsElement.appendChild(groupElement);
      });
    }

    // ---- Stage mode: component rail + focused option tray ----
    getFocusedGroup() {
      return this.groups.find((g) => g.id === this.focusedGroupId) || this.groups[0] || null;
    }

    renderTray() {
      const group = this.getFocusedGroup();
      if (!group) return;

      const existing = this.controlsElement.querySelector('.product-builder__options--tray');
      if (existing && this._trayGroupId === group.id) {
        // Same component — just update which card is selected (keeps scroll + images).
        this.updateTraySelection(group, existing);
        return;
      }

      // Fresh build for a newly focused component.
      this.controlsElement.innerHTML = '';

      const wrap = document.createElement('div');
      wrap.className = 'product-builder__tray-wrap';

      const tray = document.createElement('div');
      tray.className = 'product-builder__options product-builder__options--tray';
      if (!group.required) tray.appendChild(this.createOptionButton(group, null));
      group.options.forEach((option) => {
        if (this.config.hideSoldOut && !option.purchasable && !option.displayOnly) return;
        tray.appendChild(this.createOptionButton(group, option));
      });

      wrap.appendChild(this.makeArrow('prev'));
      wrap.appendChild(tray);
      wrap.appendChild(this.makeArrow('next'));
      this.controlsElement.appendChild(wrap);

      this._trayGroupId = group.id;
      this._trayJustBuilt = true;
      this.setupTrayScrollSelect(tray, group);
      requestAnimationFrame(() => this.fitModel());
    }

    updateTraySelection(group, tray) {
      const selIds = this.getSelectedIds(group);
      tray.querySelectorAll('.product-builder__option').forEach((btn) => {
        const oid = btn.dataset.optionId;
        const isSel = oid ? selIds.includes(oid) : selIds.length === 0;
        btn.classList.toggle('is-selected', isSel);
      });
    }

    makeArrow(dir) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'product-builder__tray-arrow product-builder__tray-arrow--' + dir;
      b.setAttribute('aria-label', dir === 'prev' ? 'Previous option' : 'Next option');
      b.innerHTML = '<span aria-hidden="true"></span>';
      b.addEventListener('click', () => this.stepTray(dir === 'prev' ? -1 : 1));
      return b;
    }

    // Desktop arrows: move the selection one option left/right within the component.
    stepTray(delta) {
      const group = this.getFocusedGroup();
      if (!group || group.allowMultiple) return;
      const opts = group.options.filter((o) => !(this.config.hideSoldOut && !o.purchasable && !o.displayOnly));
      const list = group.required ? opts : [null].concat(opts);
      if (!list.length) return;
      const selIds = this.getSelectedIds(group);
      let idx = list.findIndex((o) => (o ? selIds.includes(o.id) : selIds.length === 0));
      if (idx === -1) idx = 0;
      let next = idx + delta;
      if (next < 0) next = 0;
      if (next > list.length - 1) next = list.length - 1;
      const target = list[next];
      if (target === null) this.selectedByGroup[group.id] = [];
      else { this.selectedByGroup[group.id] = [target.id]; this.applyLinkedVariant(target); }
      this.clearMessages();
      this.render();
    }

    // Mobile/coverflow: whichever card lands in the centre becomes the selection.
    // Runs once per animation frame while scrolling (no settle delay) so the
    // model swaps live as each card passes the centre.
    setupTrayScrollSelect(tray, group) {
      if (group.allowMultiple) return;
      let ticking = false;
      tray.addEventListener('scroll', () => {
        if (this._skipScrollSelect || ticking) return;
        ticking = true;
        requestAnimationFrame(() => { ticking = false; this.selectCenteredCard(tray, group); });
      }, { passive: true });
    }

    selectCenteredCard(tray, group) {
      if (this.isSubmitting) return;
      // Component-tab (Benchmade-match) mode is tap-to-select: rows scroll
      // freely without committing whatever lands in the middle.
      if (this.ctabsActive()) return;
      // Rows uses swipe-to-select on mobile only; desktop rows stays tap-to-select.
      if (this.optionRows && window.innerWidth > 820) return;
      const center = tray.scrollLeft + tray.clientWidth / 2;
      let best = null;
      let bestDist = Infinity;
      tray.querySelectorAll('.product-builder__option').forEach((btn) => {
        const c = btn.offsetLeft + btn.offsetWidth / 2;
        const d = Math.abs(c - center);
        if (d < bestDist) { bestDist = d; best = btn; }
      });
      if (!best) return;
      const oid = best.dataset.optionId || '';
      const selIds = this.getSelectedIds(group);
      const already = oid ? (selIds.length === 1 && selIds[0] === oid) : (selIds.length === 0);
      if (already) return;
      if (!oid) {
        this.selectedByGroup[group.id] = [];
      } else {
        const option = group.options.find((o) => o.id === oid);
        if (!option) return;
        this.selectedByGroup[group.id] = [oid];
        this.applyLinkedVariant(option);
      }
      // Lightweight live update — model + selection + total + rail, no full
      // re-render and no re-centre (the user is driving the scroll).
      this.clearMessages();
      this.renderPreview();
      this.updateTraySelection(group, tray);
      this.renderTotal();
      this.updateAddButton();
      if (this.stageMode) { this.renderTrayHead(); this.updateNavStates(); }
      else { this.updateRowPick(group); }
      // Component-tab mode: the finish strip and chip states track the
      // scroll-committed selection live, not just on click.
      if (this.ctabsActive()) {
        this.updateVariantStrip(group);
        this.renderComponentTabs();
      }
    }

    // Rebuild only the variant strip of one group's row to match the current
    // selection (used by scroll-to-select, where a full re-render would fight
    // the user's scroll).
    updateVariantStrip(group) {
      const row = this.controlsElement.querySelector('.product-builder__row[data-group-id="' + group.id + '"]');
      if (!row) return;
      const old = row.querySelector('.product-builder__vstrip');
      const cluster = this.clusterGroupOptions(group);
      const fresh = (cluster && cluster.activeFamily)
        ? this.buildVariantStrip(group, cluster.activeFamily)
        : this.buildEmptyStrip();
      if (old) old.replaceWith(fresh);
      else row.appendChild(fresh);
    }

    // Section navigation, rendered into the nav container ([data-builder-rail]).
    // Two compact styles behind an internal flag (navStyle):
    // pills — slim scrollable row of text labels; active accented; ✓ when set
    // stepper — big current-section name + prev/next arrows + progress dots
    renderNav() {
      if (!this.railElement) return;
      if (this.navStyle === 'stepper') this.renderStepper();
      else this.renderPills();
    }

    updateNavStates() {
      if (this.navStyle === 'stepper') this.renderStepper();
      else this.updatePillStates();
    }

    // Customer-facing Freestyle (pills) / Guided (stepper) switch.
    renderNavToggle() {
      const host = this.navToggleElement;
      if (!host) return;
      if (!this.navToggle) { host.innerHTML = ''; return; }

      if (host.querySelector('.product-builder__navtoggle-btn')) {
        host.querySelectorAll('.product-builder__navtoggle-btn').forEach((b) => {
          b.classList.toggle('is-active', b.dataset.style === this.navStyle);
          b.setAttribute('aria-pressed', b.dataset.style === this.navStyle ? 'true' : 'false');
        });
        return;
      }

      host.innerHTML = '';
      const seg = document.createElement('div');
      seg.className = 'product-builder__navtoggle';
      [['pills', this.navLabelFree], ['stepper', this.navLabelGuided]].forEach((pair) => {
        const style = pair[0];
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'product-builder__navtoggle-btn';
        btn.dataset.style = style;
        btn.textContent = pair[1];
        const on = style === this.navStyle;
        if (on) btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.addEventListener('click', () => this.setNavStyle(style));
        seg.appendChild(btn);
      });
      host.appendChild(seg);
    }

    setNavStyle(style) {
      if ((style !== 'pills' && style !== 'stepper') || style === this.navStyle) return;
      this.navStyle = style;
      try { window.localStorage.setItem('pb_nav_style', style); } catch (e) { /* private mode */ }
      this.renderNav();        // rebuilds the nav in the new style
      this.renderNavToggle();  // update the toggle's active state
    }

    renderPills() {
      const nav = this.railElement;
      nav.classList.remove('product-builder__nav--stepper');
      nav.classList.add('product-builder__nav--pills');
      if (nav.querySelector('.product-builder__pill')) {
        this.updatePillStates();
        this.ensureActivePillVisible(false);
        return;
      }
      nav.innerHTML = '';
      this.groups.forEach((group) => {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'product-builder__pill';
        pill.dataset.groupId = group.id;
        const label = document.createElement('span');
        label.className = 'product-builder__pill-label';
        label.textContent = this.cleanLabel(group.summaryLabel || group.label);
        const check = document.createElement('span');
        check.className = 'product-builder__pill-check';
        check.setAttribute('aria-hidden', 'true');
        check.textContent = '✓';
        pill.append(label, check);
        pill.addEventListener('click', () => this.focusGroup(group.id));
        nav.appendChild(pill);
      });
      this.updatePillStates();
      requestAnimationFrame(() => this.ensureActivePillVisible(true));
    }

    updatePillStates() {
      const nav = this.railElement;
      this.groups.forEach((group) => {
        const pill = nav.querySelector('.product-builder__pill[data-group-id="' + group.id + '"]');
        if (!pill) return;
        const selected = this.getSelectedOptionsForGroup(group);
        pill.classList.toggle('is-active', group.id === this.focusedGroupId);
        pill.classList.toggle('has-selection', selected.length > 0);
        pill.classList.toggle('is-missing', group.required && !selected.length);
      });
    }

    ensureActivePillVisible(instant) {
      const nav = this.railElement;
      const active = nav.querySelector('.product-builder__pill.is-active');
      if (!active) return;
      const left = active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2;
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      nav.scrollTo({ left: Math.max(0, left), behavior: (instant || reduce) ? 'auto' : 'smooth' });
    }

    renderStepper() {
      const nav = this.railElement;
      nav.classList.remove('product-builder__nav--pills');
      nav.classList.add('product-builder__nav--stepper');
      nav.innerHTML = '';

      const idx = this.groups.findIndex((g) => g.id === this.focusedGroupId);
      const group = this.groups[idx] || this.groups[0];

      const bar = document.createElement('div');
      bar.className = 'product-builder__stepper-bar';

      const prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'product-builder__stepper-arrow product-builder__stepper-arrow--prev';
      prev.setAttribute('aria-label', 'Previous section');
      prev.innerHTML = '<span aria-hidden="true"></span>';
      prev.disabled = idx <= 0;
      prev.addEventListener('click', () => this.stepSection(-1));

      const titleWrap = document.createElement('div');
      titleWrap.className = 'product-builder__stepper-titlewrap';
      const title = document.createElement('div');
      title.className = 'product-builder__stepper-title';
      title.textContent = group ? (group.summaryLabel || group.label) : '';
      const count = document.createElement('div');
      count.className = 'product-builder__stepper-count';
      count.textContent = (idx + 1) + ' of ' + this.groups.length;
      titleWrap.append(title, count);

      const next = document.createElement('button');
      next.type = 'button';
      next.className = 'product-builder__stepper-arrow product-builder__stepper-arrow--next';
      next.setAttribute('aria-label', 'Next section');
      next.innerHTML = '<span aria-hidden="true"></span>';
      next.disabled = idx >= this.groups.length - 1;
      next.addEventListener('click', () => this.stepSection(1));

      bar.append(prev, titleWrap, next);

      const dots = document.createElement('div');
      dots.className = 'product-builder__stepper-dots';
      this.groups.forEach((g) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'product-builder__stepper-dot';
        dot.setAttribute('aria-label', g.summaryLabel || g.label);
        if (g.id === this.focusedGroupId) dot.classList.add('is-active');
        const sel = this.getSelectedOptionsForGroup(g);
        if (sel.length) dot.classList.add('has-selection');
        if (g.required && !sel.length) dot.classList.add('is-missing');
        dot.addEventListener('click', () => this.focusGroup(g.id));
        dots.appendChild(dot);
      });

      nav.append(bar, dots);
    }

    stepSection(delta) {
      const idx = this.groups.findIndex((g) => g.id === this.focusedGroupId);
      let ni = idx + delta;
      if (ni < 0) ni = 0;
      if (ni > this.groups.length - 1) ni = this.groups.length - 1;
      const g = this.groups[ni];
      if (g) this.focusGroup(g.id);
    }

    // Focus a component (show its options in the tray). Does NOT change any
    // selection or the model.
    focusGroup(groupId) {
      if (this.isSubmitting || !groupId) return;
      this.focusedGroupId = groupId;
      this.renderTrayHead();
      this.renderTray();
      this.renderNav();
      requestAnimationFrame(() => this.centerTraySelection());
    }

    nextGroupId() {
      const idx = this.groups.findIndex((g) => g.id === this.focusedGroupId);
      return (idx >= 0 && idx < this.groups.length - 1) ? this.groups[idx + 1].id : null;
    }

    prevGroupId() {
      const idx = this.groups.findIndex((g) => g.id === this.focusedGroupId);
      return (idx > 0) ? this.groups[idx - 1].id : null;
    }

    // Scroll the tray so the selected option (or the first, if none) sits centered.
    // Instant when the component just changed; smooth when re-centering a pick;
    // skipped entirely when the user themselves is scrolling (scroll-to-select).
    centerTraySelection() {
      if (this._skipCenter) { this._skipCenter = false; return; }
      const tray = this.controlsElement.querySelector('.product-builder__options--tray');
      if (!tray) return;
      const target = tray.querySelector('.product-builder__option.is-selected') || tray.firstElementChild;
      if (!target) return;
      const left = target.offsetLeft - (tray.clientWidth - target.offsetWidth) / 2;
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = (this._trayJustBuilt || reduce) ? 'auto' : 'smooth';
      this._trayJustBuilt = false;
      // Suppress scroll-to-select while our own programmatic scroll is animating.
      this._skipScrollSelect = true;
      tray.scrollTo({ left: Math.max(0, left), behavior: behavior });
      clearTimeout(this._scrollSelectTimer);
      this._scrollSelectTimer = window.setTimeout(() => { this._skipScrollSelect = false; }, behavior === 'auto' ? 60 : 440);
    }

    // Fill an element with a component's current pick + price (or a prompt when
    // nothing is chosen). Shared by the stage pick line and each rows-mode row.
    fillPick(pick, group) {
      pick.innerHTML = '';
      const sel = this.getSelectedOptionsForGroup(group)[0];
      if (sel) {
        const name = document.createElement('span');
        name.className = 'product-builder__pick-name';
        name.textContent = sel.label;
        pick.appendChild(name);

        let priceText = '';
        if (sel.displayOnly || sel.priceCents === 0) priceText = 'Included';
        else if (this.relativePricing && sel.priceCents != null) {
          const delta = this.priceParts(sel, group).effective - this.baseCents(group);
          priceText = delta === 0 ? 'Included' : (delta > 0 ? '+' + this.formatMoney(delta) : '−' + this.formatMoney(-delta));
        } else if (sel.priceCents != null) {
          const pp = this.priceParts(sel, group);
          priceText = '+' + this.formatMoney(pp.effective);
        }
        if (priceText) {
          const price = document.createElement('span');
          price.className = 'product-builder__pick-price';
          price.textContent = priceText;
          pick.appendChild(price);
        }
        if (!sel.purchasable && !sel.displayOnly) {
          const so = document.createElement('span');
          so.className = 'product-builder__pick-soldout';
          so.textContent = 'Sold out';
          pick.appendChild(so);
        }
      } else {
        const prompt = document.createElement('span');
        prompt.className = 'product-builder__pick-prompt';
        prompt.textContent = group.required
          ? ('Choose your ' + this.cleanLabel(group.summaryLabel || group.label))
          : ('Add ' + this.cleanLabel(group.summaryLabel || group.label));
        pick.appendChild(prompt);
      }
    }

    // The line above the options shows the focused component's current pick and
    // its price (price now lives here + in the total, not on every card).
    renderTrayHead() {
      if (!this.trayHeadElement) return;
      this.trayHeadElement.innerHTML = '';
      const group = this.getFocusedGroup();
      if (!group) return;

      const pick = document.createElement('div');
      pick.className = 'product-builder__pick';
      this.fillPick(pick, group);
      this.trayHeadElement.appendChild(pick);
      if (this.showsMultiControl(group)) this.trayHeadElement.appendChild(this.createMultiControl(group));
      if (group.note) {
        const noteEl = document.createElement('div');
        noteEl.className = 'product-builder__group-note';
        noteEl.innerHTML = group.note;
        this.trayHeadElement.appendChild(noteEl);
      }
    }

    // ---- Rows mode: one horizontal option slider per component ----
    renderRows() {
      if (this.ctabsActive()) {
        // Component-tab mode rebuilds from scratch every render: only one row is
        // visible, and family cards/variant strips depend on the current selection.
        if (!this._frameExited) this.setFrame(true);
        const sections = this.navSectionsCtabs();
        if (!sections.some((sec) => sec.id === this.focusedGroupId)) {
          this.focusedGroupId = sections[0] ? sections[0].id : null;
        }
        this.controlsElement.innerHTML = '';
        this.controlsElement.dataset.builtMode = 'ctabs';
        const promos = [this.config.discountBanner, this.config.discountBanner2, this.config.discountBanner3]
          .map((t) => (t || '').trim()).filter(Boolean);
        if (promos.length) {
          const wrap = document.createElement('div');
          wrap.className = 'product-builder__offers' + (this._offersOpen ? ' is-open' : '');
          const toggle = document.createElement('button');
          toggle.type = 'button';
          toggle.className = 'product-builder__offers-toggle';
          toggle.innerHTML = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>'
            + '<span>' + promos.length + (promos.length === 1 ? ' offer' : ' offers') + ' on this build</span>'
            + '<svg class="product-builder__offers-chevron" viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M4 6l4 4 4-4"/></svg>';
          const list = document.createElement('div');
          list.className = 'product-builder__offers-list';
          list.hidden = !this._offersOpen;
          promos.forEach((text) => {
            const item = document.createElement('div');
            item.className = 'product-builder__offers-item';
            item.textContent = text;
            list.appendChild(item);
          });
          toggle.addEventListener('click', () => {
            this._offersOpen = !this._offersOpen;
            list.hidden = !this._offersOpen;
            wrap.classList.toggle('is-open', this._offersOpen);
            this.fitFrameModel();
          });
          wrap.append(toggle, list);
          this.controlsElement.appendChild(wrap);
        }
        sections.forEach((sec) => this.controlsElement.appendChild(this.buildNavSectionRow(sec)));
        this.renderComponentTabs();
        this.applyActiveRow();
        requestAnimationFrame(() => this.fitFrameModel());
        return;
      }
      // Rows built by component-tab mode contain family cards/strips — rebuild
      // rather than update if the mode has changed since they were built.
      if (this.controlsElement.dataset.builtMode !== 'ctabs' && this.controlsElement.querySelector('.product-builder__row')) {
        this.groups.forEach((group) => this.updateRow(group));
        return;
      }
      this.clearComponentTabs();
      this.controlsElement.innerHTML = '';
      this.controlsElement.dataset.builtMode = 'rows';
      this.groups.forEach((group) => this.controlsElement.appendChild(this.buildRow(group)));
    }

    // Remove the ctabs chip bar if it is what currently occupies the tray head
    // (never touches stage mode's tray-head content), and put a relocated
    // tray-head back into the controls column.
    clearComponentTabs() {
      this.setFrame(false);
      const randRow = this.section.querySelector('.product-builder__randomize-row');
      if (this.randomizeButton && randRow && this.randomizeButton.parentElement !== randRow) {
        this.randomizeButton.classList.remove('product-builder__randomize-button--top');
        randRow.appendChild(this.randomizeButton);
      }
      if (this.trayHeadElement && this.trayHeadElement.querySelector('.product-builder__ctabs')) {
        this.trayHeadElement.innerHTML = '';
      }
      const col = this.section.querySelector('.product-builder__controls-column');
      if (this.trayHeadElement && col && this.trayHeadElement.parentElement !== col) {
        col.insertBefore(this.trayHeadElement, this.controlsElement);
      }
    }

    // Top-level navigation for component-tab mode: each REQUIRED group gets its
    // own tab; all optional groups bundle into one "Accessories" tab.
    navSectionsCtabs() {
      const main = this.groups.filter((g) => g.required);
      const acc = this.groups.filter((g) => !g.required);
      const sections = main.map((g) => ({ id: g.id, label: this.cleanLabel(g.summaryLabel || g.label), groups: [g] }));
      if (acc.length) sections.push({ id: '__acc', label: 'Accessories', groups: acc });
      return sections;
    }

    // Component-tab ("custom selection") mode: mobile thumbs only, opted in via
    // the section class (setting or ?bsel=tabs). Stage mode has its own nav.
    ctabsActive() {
      // width-agnostic: the class decides (desktop gets the match design too)
      return !this.stageMode && this.section.classList.contains('product-builder--ctabs');
    }

    // Fixed-viewport takeover (Benchmade app frame): the section covers the
    // screen and the page behind cannot scroll. Mobile ctabs+mtop only.
    setFrame(on) {
      if (!this.section.classList.contains('product-builder--mtop')) on = false;
      this.section.classList.toggle('product-builder--frame', !!on);
      document.body.classList.toggle('builder-ctabs-lock', !!on);
      if (on) requestAnimationFrame(() => this.fitFrameModel());
      if (!ProductBuilder._frameResizeBound) {
        ProductBuilder._frameResizeBound = true;
        let t;
        const refit = () => {
          clearTimeout(t);
          t = window.setTimeout(() => {
            document.querySelectorAll('[data-product-builder-section]').forEach((sec) => {
              if (sec.__builder) sec.__builder.fitFrameModel();
            });
          }, 120);
        };
        window.addEventListener('resize', refit);
        window.addEventListener('orientationchange', refit);
      }
    }

    // iOS Safari zeroes percentage-height + aspect-ratio chains inside nested
    // flex, so the frame's model square is sized explicitly from the measured
    // column instead of CSS.
    fitFrameModel() {
      if (!this.section.classList.contains('product-builder--frame')) return;
      const col = this.section.querySelector('.product-builder__preview-column');
      const preview = this.previewElement;
      if (!col || !preview) return;
      const w = col.clientWidth, h = col.clientHeight;
      if (!w || !h) return;
      // hydro's black-stage glass bleeds past its window (behind the floating
      // tabs, through the frosted sheet); the white zones stay clipped
      const build = this.section.getAttribute('data-build');
      // per-build trim, since each build's art fills its square differently
      const TRIM = { ti: 0.86 };
      const trim = TRIM[build] || 1;
      const side = Math.max(120, Math.round(Math.min(w * 0.88, h - 8) * trim));
      preview.style.width = side + 'px';
      preview.style.height = side + 'px';
      this.publishClusterWidth();
    }

    // On desktop the switcher + BUY NOW are pinned to the top-right corner, in
    // the same band as the cart icon. Publish their measured width so the tabs
    // row can reserve it — the BUY label carries the price, so the cluster's
    // width changes with the build and a hard-coded reserve would drift.
    publishClusterWidth() {
      // Measure whichever build is ON SCREEN, not necessarily this instance's.
      // Inactive sections keep a measurable card, so each instance publishing
      // its own width meant the last one to fire won — leaving the reserve at
      // another build's price width. Observers fire after layout, so the
      // active class is always set by the time we read it.
      const active = document.querySelector('[data-product-builder-section].is-active-build') || this.section;
      const card = active.querySelector('.product-builder__summary-card');
      if (!card) return;
      // Observe THIS instance's own card, so its show/hide on a build switch is
      // itself a size change that fires the callback. Observing the active
      // card instead would leave this instance blind to its own activation.
      // Beats remembering to call this by hand: the cluster also resizes on
      // price changes and font loads, and any missed call leaves the tab bar
      // running under it. Setting a CSS var can't resize the card, so this
      // can't feed back on itself.
      if (!this._clusterRO && typeof ResizeObserver !== 'undefined') {
        const own = this.section.querySelector('.product-builder__summary-card');
        if (own) {
          this._clusterRO = new ResizeObserver(() => this.publishClusterWidth());
          this._clusterRO.observe(own);
        }
      }
      // Published on the ROOT, not the section: each build has its own section,
      // and a section that never ran this left the var undefined, which makes
      // the calc invalid and silently drops the rule that depends on it.
      const w = Math.ceil(card.getBoundingClientRect().width);
      if (w > 0) document.documentElement.style.setProperty('--pb-cluster-w', w + 'px');
    }

    // Benchmade-style... menu next to BUY NOW: Randomize (stays open for
    // repeat taps) + links to the other builders (via the build switcher's
    // own tab buttons, so all switcher behavior holds).
    ensureMoreMenu() {
      const bar = this.section.querySelector('.product-builder__summary-card');
      if (!bar || bar.querySelector('.product-builder__more-toggle')) return;
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'product-builder__more-toggle';
      toggle.setAttribute('aria-label', 'More options');
      toggle.innerHTML = '';
      // name the current build so the... reads as a switcher (shown on desktop)
      const curKey = this.section.getAttribute('data-build');
      const curTab = document.querySelector('[data-build-switcher] [data-build-target="' + curKey + '"] .build-switcher__tab-label');
      if (curTab && curTab.textContent.trim()) {
        const nameEl = document.createElement('span');
        nameEl.className = 'product-builder__more-build';
        nameEl.textContent = curTab.textContent.trim();
        toggle.appendChild(nameEl);
        toggle.setAttribute('aria-label', 'Switch builder, currently ' + curTab.textContent.trim());
      }
      const chev = document.createElement('span');
      chev.className = 'product-builder__more-chevron';
      chev.innerHTML = '<svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M4 6l4 4 4-4"/></svg>';
      toggle.appendChild(chev);

      const menu = document.createElement('div');
      menu.className = 'product-builder__more-menu';
      menu.hidden = true;

      // builder switchers on top (rare, context-resetting taps stay far from
      // the thumb); Randomize at the bottom, nearest the thumb for repeat rolls
      const switcherTabs = document.querySelectorAll('[data-build-switcher] [data-build-target]');
      if (switcherTabs.length > 1) {
        const label = document.createElement('div');
        label.className = 'product-builder__more-label product-builder__more-label--first';
        label.textContent = 'Switch builder';
        menu.appendChild(label);
        switcherTabs.forEach((tab) => {
          const key = tab.getAttribute('data-build-target');
          const item = document.createElement('button');
          item.type = 'button';
          item.className = 'product-builder__more-item';
          if (key === this.section.getAttribute('data-build')) item.classList.add('is-current');
          const name = tab.querySelector('.build-switcher__tab-label');
          item.innerHTML = '<span>' + ((name && name.textContent.trim()) || key) + '</span>';
          item.addEventListener('click', () => { tab.click(); });
          menu.appendChild(item);
        });
      }

      // The corner cluster owns the top right, so the cart lives in here now.
      // Sits above Randomize, which stays closest to the thumb for repeat rolls.
      const cartItem = document.createElement('button');
      cartItem.type = 'button';
      cartItem.className = 'product-builder__more-item product-builder__more-item--divided product-builder__more-item--cart';
      cartItem.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="20" r="1.6"/><circle cx="17" cy="20" r="1.6"/><path d="M3 3h2l2.6 12.5a1.5 1.5 0 0 0 1.5 1.2h7.6a1.5 1.5 0 0 0 1.5-1.2L20.5 8H6"/></svg><span>View cart</span>';
      cartItem.addEventListener('click', () => {
        menu.hidden = true;
        this.openCart();
      });
      menu.appendChild(cartItem);

      const randomize = document.createElement('button');
      randomize.type = 'button';
      randomize.className = 'product-builder__more-item product-builder__more-item--divided product-builder__more-item--randomize';
      randomize.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 3h5v5"/><path d="M4 20L21 3"/><path d="M21 16v5h-5"/><path d="M15 15l6 6"/><path d="M4 4l5 5"/></svg><span>Randomize</span>';
      randomize.addEventListener('click', () => {
        // direct call (a synthetic.click would bubble to the outside-click
        // closer) — menu stays open for repeat taps
        if (!this.isSubmitting) this.handleRandomize();
      });
      menu.appendChild(randomize);

      // Start over: back to the authored defaults, not an empty build. Sits by
      // Randomize because it's the same kind of action — undoing one.
      const reset = document.createElement('button');
      reset.type = 'button';
      reset.className = 'product-builder__more-item product-builder__more-item--reset';
      reset.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg><span>Start over</span>';
      reset.addEventListener('click', () => {
        if (this.isSubmitting) return;
        this.resetBuild();
      });
      menu.appendChild(reset);


      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        menu.hidden = !menu.hidden;
      });
      document.addEventListener('click', (e) => {
        if (!menu.hidden && !menu.contains(e.target) && e.target !== toggle && !toggle.contains(e.target)) {
          menu.hidden = true;
        }
      });

      // toggle + menu share a positioned wrapper so the dropdown always hangs
      // off the switcher itself, whatever the bar's alignment
      const wrap = document.createElement('div');
      wrap.className = 'product-builder__more-wrap';
      wrap.append(toggle, menu);
      bar.appendChild(wrap);
    }

    // The drawer is z-lifted above the frame (CSS), so the builder stays put.
    openCart() {
      const cart = document.getElementById('mini-cart');
      if (cart) cart.open = true;
      else window.location.href = (this.config.rootUrl || '/').replace(/\/$/, '') + '/cart';
    }

    renderComponentTabs() {
      if (!this.trayHeadElement) return;
      // Benchmade placement: with the model on top, the chip bar lives ABOVE the
      // model (first child of the inner flow), not inside the controls sheet.
      const inner = this.section.querySelector('.product-builder__inner');
      const isMtop = this.section.classList.contains('product-builder--mtop');
      if (inner && isMtop && this.trayHeadElement.parentElement !== inner) {
        inner.insertBefore(this.trayHeadElement, inner.firstChild);
      }
      this.trayHeadElement.innerHTML = '';
      const row = document.createElement('div');
      row.className = 'product-builder__ctabs-row';
      if (isMtop) {
        // Three states for this slot, ratified 2026-09-01:
        //   builder page (not contained) -> the S, linking home. That page IS
        //     the builder, so there is nowhere to exit TO; home is the only way
        //     out, and the logo is the right affordance for it.
        //   inline, collapsed (contained) -> an EXPAND icon. The logo used to
        //     sit here and shoppers read it as "go home", which threw the build
        //     away; on the homepage it reloaded the page they were already on.
        //   any full screen state -> the word EXIT, drawn by CSS (it hides this
        //     button's children and fills ::after), escaping back to whichever
        //     page opened it. Not handled here on purpose.
        // The inline control is a real BUTTON rather than an <a> neutered after
        // the fact: renderComponentTabs() rebuilds this element on every render,
        // which drops the section script's pbExpandWired flag, and the re-wire
        // only runs on load and 60ms after a click. In that window an <a> is
        // live and navigates. A button has nothing to navigate to.
        const contained = this.section.classList.contains('product-builder--contained');
        const toggle = document.createElement(contained ? 'button' : 'a');
        toggle.className = 'product-builder__frame-toggle';
        if (contained) {
          toggle.type = 'button';
          const expanded = this.section.classList.contains('pb-expanded');
          toggle.setAttribute('aria-label', expanded ? 'Exit full screen' : 'Expand the builder to full screen');
          toggle.classList.add('product-builder__frame-toggle--expand');
          // Sized in product-builder.css, NOT with an inline style: when this
          // builder is expanded the CSS hides the button's children so ::after
          // can draw EXIT, and an inline display would outrank that rule and
          // leave the icon drawn on top of the word.
          toggle.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></svg>';
        } else {
          toggle.href = this.config.rootUrl || '/';
          toggle.setAttribute('aria-label', 'Back to Simrell');
          if (this.config.brandLogo) {
            toggle.classList.add('product-builder__frame-toggle--logo');
            const logo = document.createElement('img');
            logo.src = this.config.brandLogo;
            logo.alt = 'Simrell';
            toggle.appendChild(logo);
          } else {
            toggle.textContent = '\u2715';
          }
        }
        row.appendChild(toggle);
      }
      const bar = document.createElement('div');
      bar.className = 'product-builder__ctabs';
      this.navSectionsCtabs().forEach((sec) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'product-builder__ctab';
        chip.textContent = sec.label;
        if (sec.id === this.focusedGroupId) chip.classList.add('is-active');
        if (sec.groups.some((g) => this.getSelectedIds(g).length)) chip.classList.add('is-done');
        chip.addEventListener('click', () => {
          this.focusedGroupId = sec.id;
          this.renderComponentTabs();
          this.applyActiveRow();
        });
        bar.appendChild(chip);
      });
      row.appendChild(bar);
      // Benchmade's top-right slot: OUR cart button — view/adjust the cart
      // from inside the builder. Randomize lives in the buy bar (their
      // three-dot slot).
      if (isMtop) {
        const cartBtn = document.createElement('button');
        cartBtn.type = 'button';
        cartBtn.className = 'product-builder__cart-button';
        cartBtn.setAttribute('aria-label', 'View cart');
        cartBtn.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="20" r="1.6"/><circle cx="17" cy="20" r="1.6"/><path d="M3 3h2l2.6 12.5a1.5 1.5 0 0 0 1.5 1.2h7.6a1.5 1.5 0 0 0 1.5-1.2L20.5 8H6"/></svg>';
        cartBtn.addEventListener('click', () => this.openCart());
        row.appendChild(cartBtn);
      }
      this.trayHeadElement.appendChild(row);
      // edge fades signal hidden tabs on whichever side has more content
      const updateClip = () => {
        const max = bar.scrollWidth - bar.clientWidth - 1;
        bar.classList.toggle('is-clipped-left', max > 0 && bar.scrollLeft > 1);
        bar.classList.toggle('is-clipped-right', max > 0 && bar.scrollLeft < max);
      };
      bar.addEventListener('scroll', updateClip, { passive: true });
      requestAnimationFrame(() => {
        const active = bar.querySelector('.is-active');
        if (active) bar.scrollTo({ left: Math.max(0, active.offsetLeft - (bar.clientWidth - active.offsetWidth) / 2), behavior: 'auto' });
        updateClip();
      });
    }

    applyActiveRow() {
      const rows = this.controlsElement.querySelectorAll('.product-builder__row');
      rows.forEach((row) => row.classList.toggle('is-active-row', row.dataset.groupId === this.focusedGroupId));
      const activeSlider = this.controlsElement.querySelector('.product-builder__row.is-active-row .product-builder__options--tray');
      if (activeSlider) requestAnimationFrame(() => {
        this.centerSlider(activeSlider, true);
        // rows are built hidden, so their edge arrows measured zero widths —
        // re-run the arrow visibility check now that this row is shown
        activeSlider.dispatchEvent(new Event('scroll'));
      });
    }

    // Cluster a group's options into variant families: options that point at the
    // same product (2+ of them, each with a concrete variant) collapse into one
    // tray card whose variants are picked from a strip below the tray. Options
    // without a product/variant (legacy blocks) always stay standalone cards.
    // Multi-select groups stay flat — family state would be ambiguous there.
    clusterGroupOptions(group) {
      if (group.allowMultiple) return null;
      const pickable = (o) => this.config.hideSoldOut ? (o.purchasable || o.displayOnly) : true;
      const byProduct = new Map();
      group.options.forEach((o) => {
        if (!o.productHandle || !o.variantId || !pickable(o)) return;
        if (!byProduct.has(o.productHandle)) byProduct.set(o.productHandle, []);
        byProduct.get(o.productHandle).push(o);
      });
      const families = new Map();
      byProduct.forEach((members, handle) => { if (members.length >= 2) families.set(handle, members); });
      if (!families.size) return null;

      const selected = this.getSelectedOptionsForGroup(group)[0] || null;
      const items = [];
      const seen = new Set();
      group.options.forEach((o) => {
        const fam = o.productHandle ? families.get(o.productHandle) : null;
        if (!fam || !fam.includes(o)) { items.push({ option: o }); return; }
        if (seen.has(o.productHandle)) return;
        seen.add(o.productHandle);
        const rep = (selected && fam.includes(selected)) ? selected : fam[0];
        items.push({ family: fam, rep: rep, label: this.familyLabel(fam) });
      });
      const activeFamily = (selected && selected.productHandle && families.has(selected.productHandle) && families.get(selected.productHandle).includes(selected))
        ? families.get(selected.productHandle) : null;
      return { items: items, activeFamily: activeFamily };
    }

    familyLabel(members) {
      const labels = members.map((m) => this.cleanLabel(m.label));
      let prefix = labels[0];
      for (let i = 1; i < labels.length; i++) {
        while (prefix && labels[i].slice(0, prefix.length) !== prefix) prefix = prefix.slice(0, -1);
      }
      prefix = prefix.replace(/[\s\-–—·|,]+$/, '').trim();
      if (prefix.length >= 3) return prefix;
      // Labels share no useful prefix (e.g. plain "Polished"/"Sandblasted") —
      // fall back to the parent product's title, minus the catalog's
      // "Family: " prefix ("Simrell: Titanium Vortex Stem" -> the stem part).
      const titled = members.find((m) => m.productTitle);
      if (!titled) return labels[0];
      return this.cleanLabel(titled.productTitle).replace(/^[^:]{2,30}:\s*/, '');
    }

    variantChipLabel(option, famLabel) {
      const full = this.cleanLabel(option.label);
      const rest = full.slice(0, famLabel.length) === famLabel
        ? full.slice(famLabel.length).replace(/^[\s\-–—·|,]+/, '').trim()
        : '';
      return rest || full;
    }

    buildFamilyButton(group, item) {
      const btn = this.createOptionButton(group, item.rep);
      btn.classList.add('is-family');
      if (item.family.some((m) => this.getSelectedIds(group).includes(m.id))) btn.classList.add('is-selected');
      const lbl = btn.querySelector('.product-builder__option-label');
      if (lbl) lbl.textContent = item.label;
      const badge = document.createElement('span');
      badge.className = 'product-builder__family-count';
      badge.textContent = item.family.length + ' finishes';
      btn.appendChild(badge);
      return btn;
    }

    // Placeholder that holds the finish-strip zone open so every component's
    // sheet is the same height (Benchmade's constant bottom sheet).
    buildEmptyStrip() {
      const strip = document.createElement('div');
      strip.className = 'product-builder__vstrip product-builder__vstrip--empty';
      return strip;
    }

    buildVariantStrip(group, family) {
      const strip = document.createElement('div');
      strip.className = 'product-builder__vstrip';
      const famLabel = this.familyLabel(family);
      const title = document.createElement('span');
      title.className = 'product-builder__vstrip-label';
      title.textContent = famLabel;
      strip.appendChild(title);
      const rowEl = document.createElement('div');
      rowEl.className = 'product-builder__vstrip-options';
      family.forEach((m) => {
        const b = this.createOptionButton(group, m);
        b.classList.add('product-builder__vchip');
        const lbl = b.querySelector('.product-builder__option-label');
        if (lbl) lbl.textContent = this.variantChipLabel(m, famLabel);
        rowEl.appendChild(b);
      });
      strip.appendChild(rowEl);
      return strip;
    }

    buildRow(group) {
      const row = document.createElement('div');
      row.className = 'product-builder__row';
      row.dataset.groupId = group.id;

      const head = document.createElement('div');
      head.className = 'product-builder__row-head';
      const label = document.createElement('span');
      label.className = 'product-builder__row-label';
      // One word per line — matches the desktop stacked title.
      this.cleanLabel(group.summaryLabel || group.label).split(/\s+/).filter(Boolean).forEach((word) => {
        const w = document.createElement('span');
        w.className = 'product-builder__group-title-word';
        w.textContent = word;
        label.appendChild(w);
      });
      const pick = document.createElement('span');
      pick.className = 'product-builder__pick product-builder__row-pick';
      this.fillPick(pick, group);
      head.append(label, pick);
      if (this.showsMultiControl(group)) head.appendChild(this.createMultiControl(group));

      const slider = document.createElement('div');
      slider.className = 'product-builder__options product-builder__options--tray product-builder__row-slider';
      if (!group.required) slider.appendChild(this.createOptionButton(group, null));
      group.options.forEach((option) => {
        if (this.config.hideSoldOut && !option.purchasable && !option.displayOnly) return;
        slider.appendChild(this.createOptionButton(group, option));
      });

      row.append(head);
      if (group.note) {
        const noteEl = document.createElement('div');
        noteEl.className = 'product-builder__group-note';
        noteEl.innerHTML = group.note;
        row.appendChild(noteEl);
      }
      row.append(slider);
      this.setupTrayScrollSelect(slider, group);
      requestAnimationFrame(() => this.centerSlider(slider, true));
      return row;
    }

    // One row per top-level nav section, Benchmade sheet anatomy: a category
    // strip (All + one entry per PRODUCT family — "Classic Ti", "Textured
    // Ti"...) over one row of unlabeled photo swatches; picking a category
    // reveals that product's finishes as the swatches. Accessories: categories
    // and swatches span all the optional groups, each swatch selecting within
    // its own group.
    buildNavSectionRow(sec) {
      const row = document.createElement('div');
      row.className = 'product-builder__row';
      row.dataset.groupId = sec.id;

      const fams = new Map();
      // MATERIAL_CATEGORY_GROUPS skip per-product families so everything falls
      // through to the material buckets instead: the ti sleeve range is five
      // titanium products that read far better as one "Titanium" tab than as
      // five near-identical ones (Classic Ti / Textured Ti / Argyle /...).
      // Side effect we want: with several products in a row, the swatch labels
      // stop stripping the shared prefix, so they read "Classic Ti Polished"
      // rather than a bare "Polished".
      if (!MATERIAL_CATEGORY_GROUPS.has(sec.id)) {
        sec.groups.forEach((g) => {
          this.familiesForGroup(g).forEach((members, handle) => {
            fams.set(handle, { group: g, members: members, label: this.familyLabel(members) });
          });
        });
      }

      row.appendChild(this.buildSectionCategoryStrip(sec, fams));
      const info = this.buildCategoryInfo(sec, fams);
      if (info) row.appendChild(info);
      const swatches = this.buildSectionSwatchRow(sec, fams);
      row.appendChild(this.buildSwatchScroller(swatches));
      requestAnimationFrame(() => this.centerSlider(swatches, true));
      return row;
    }

    sectionPickable(o) {
      return !(this.config.hideSoldOut && !o.purchasable && !o.displayOnly);
    }

    productLabel(option) {
      if (option.productTitle) {
        return this.cleanLabel(option.productTitle).replace(/^[^:]{2,30}:\s*/, '');
      }
      return this.cleanLabel(option.label);
    }

    // Categories for the Accessories tab. A group holding SEVERAL products
    // (e.g. three torches) collapses into ONE category under the group's name,
    // so its products sit together as swatches instead of fragmenting the band.
    // Single-product groups keep reading under the group's short name.
    sectionProducts(sec) {
      const cats = new Map();
      sec.groups.forEach((g) => {
        const byProduct = new Map();
        g.options.forEach((o) => {
          if (!this.sectionPickable(o) || !o.productHandle) return;
          if (!byProduct.has(o.productHandle)) byProduct.set(o.productHandle, []);
          byProduct.get(o.productHandle).push(o);
        });
        if (!byProduct.size) return;
        const groupName = this.cleanLabel(g.summaryLabel || g.label);
        if (byProduct.size > 1) {
          const members = [];
          byProduct.forEach((opts) => opts.forEach((o) => members.push(o)));
          cats.set('grp:' + g.id, { group: g, label: groupName, members: members });
        } else {
          byProduct.forEach((members, handle) => {
            cats.set(handle, { group: g, label: groupName, members: members });
          });
        }
      });
      return cats;
    }

    sectionSingles(sec, fams) {
      const inFamily = new Set();
      fams.forEach((f) => f.members.forEach((m) => inFamily.add(m.id)));
      const singles = [];
      sec.groups.forEach((g) => g.options.forEach((o) => {
        if (this.sectionPickable(o) && !inFamily.has(o.id)) singles.push(o);
      }));
      return singles;
    }

    // Min upcharge of a set of options vs their group's base — the number
    // Benchmade prints beside each material ("G10 $100", "$0" = included).
    categoryPrice(members, groupOf) {
      let min = null;
      members.forEach((o) => {
        if (o.displayOnly || o.priceCents === null) return;
        const g = groupOf(o);
        if (!g) return;
        const delta = this.priceParts(o, g).effective - this.baseCents(g);
        if (min === null || delta < min) min = delta;
      });
      if (min === null) return '';
      return min <= 0 ? '$0' : this.formatMoney(min).replace(/\.00$/, '');
    }

    catButton(key, label, priceText, current, onPick) {
      const b = document.createElement(priceText === undefined ? 'span' : 'button');
      if (b.tagName === 'BUTTON') b.type = 'button';
      b.className = 'product-builder__cat';
      if (key === current) b.classList.add('is-active');
      const name = document.createElement('span');
      name.textContent = label;
      b.appendChild(name);
      if (priceText) {
        const p = document.createElement('span');
        p.className = 'product-builder__cat-price';
        p.textContent = priceText;
        b.appendChild(p);
      }
      if (onPick) b.addEventListener('click', onPick);
      return b;
    }

    buildSectionCategoryStrip(sec, fams) {
      const strip = document.createElement('div');
      strip.className = 'product-builder__catstrip';
      // rebuilds reset scrollLeft — remember and restore per section so
      // tapping a far-right category doesn't snap the band back to the start
      strip.addEventListener('scroll', () => { this._catScroll[sec.id] = strip.scrollLeft; }, { passive: true });
      requestAnimationFrame(() => { if (this._catScroll[sec.id]) strip.scrollLeft = this._catScroll[sec.id]; });
      // Accessories: every product is its own category so the strip doubles as
      // an index of the add-ons.
      if (sec.id === '__acc') {
        const products = this.sectionProducts(sec);
        if (!products.size) { strip.classList.add('product-builder__catstrip--empty'); return strip; }
        // "All" leads the strip but is NOT the default — the band opens on the
        // first accessory, which keeps the per-product info copy visible and the
        // row short. All is one tap away for clicking straight down the list.
        const current = this._catFilter[sec.id] || this.defaultCatFilter(sec, fams);
        products.forEach((p, handle) => {
          strip.appendChild(this.catButton(handle, p.label, '', current, () => {
            this._catFilter[sec.id] = handle;
            this.renderControls();
          }));
        });
        // "All" last, matching the component groups.
        strip.appendChild(this.catButton('all', 'All', '', current, () => {
          this._catFilter[sec.id] = 'all';
          this.renderControls();
        }));
        return strip;
      }
      // Material-bucketed groups have no families ON PURPOSE, so they must fall
      // through to the bucket branch below rather than take the no-families
      // fallback (which would print one static title and no categories at all).
      if (!fams.size && !MATERIAL_CATEGORY_GROUPS.has(sec.id)) {
        // A "family" is one product wearing several options (chamber =
        // polished + sandblasted). A group whose options are all SEPARATE
        // products makes none — every heritage wood is its own product — and
        // the band came up blank, the only tab in the builder without a title.
        // Fall back to the group's own name so it still gets one.
        const orphans = this.sectionSingles(sec, fams);
        if (orphans.length && sec.groups.length === 1) {
          const g = sec.groups[0];
          const label = this.catButton('__static', this.cleanLabel(g.summaryLabel || g.label), undefined, '__static', null);
          label.classList.add('product-builder__cat--static');
          strip.appendChild(label);
          return strip;
        }
        strip.classList.add('product-builder__catstrip--empty');
        return strip;
      }
      const singles = this.sectionSingles(sec, fams);
      // One product family and nothing else: show its title for context, no "All".
      if (fams.size === 1 && !singles.length) {
        const only = fams.values().next().value;
        const label = this.catButton('__static', only.label, undefined, '__static', null);
        label.classList.add('product-builder__cat--static');
        strip.appendChild(label);
        return strip;
      }
      const current = this._catFilter[sec.id] || this.defaultCatFilter(sec, fams);
      const byGroup = new Map();
      sec.groups.forEach((g) => g.options.forEach((o) => byGroup.set(o.id, g)));
      const pick = (key) => () => { this._catFilter[sec.id] = key; this.renderControls(); };
      const buckets = this.sectionSingleBuckets(sec, fams);
      // Tabs are collected before rendering so "All" can be appended LAST while
      // the section still opens on the first real category.
      const tabs = [];
      const bucket = (material) => {
        if (buckets.has(material)) tabs.push({ key: 'mat:' + material, label: material });
      };
      ['Titanium', 'Wood', 'Silicone', 'Glass', 'Stainless', 'More'].forEach(bucket);
      fams.forEach((f, handle) => tabs.push({ key: handle, label: f.label }));
      bucket('Leather');
      tabs.forEach((t) => strip.appendChild(this.catButton(t.key, t.label, '', current, pick(t.key))));
      strip.appendChild(this.catButton('all', 'All', '', current, pick('all')));
      return strip;
    }

    // The tab a section opens on: the first REAL category, never "All" (which
    // now sits at the far right). The strip, the option rows and the info panel
    // all read this — if they disagree the band highlights one tab while the
    // rows show another.
    defaultCatFilter(sec, fams) {
      if (sec.id === '__acc') {
        const products = this.sectionProducts(sec);
        return products.size ? products.keys().next().value : 'all';
      }
      const buckets = this.sectionSingleBuckets(sec, fams);
      const order = ['Titanium', 'Wood', 'Silicone', 'Glass', 'Stainless', 'More'];
      for (let i = 0; i < order.length; i++) {
        if (buckets.has(order[i])) return 'mat:' + order[i];
      }
      if (fams.size) return fams.keys().next().value;
      if (buckets.has('Leather')) return 'mat:Leather';
      return 'all';
    }

    // Info panel for the active category when it maps to one product with a
    // description (Benchmade's steel-education idea at our product level).
    buildCategoryInfo(sec, fams) {
      // panels render ONLY where a note is authored (assemblies for now) —
      // no description fallback, so coverage grows by editing the theme
      const infoOf = (members) => {
        const curated = members.find((m) => m.optionInfo);
        return curated ? curated.optionInfo : null;
      };
      const highlightOf = (members) => {
        const h = members.find((m) => m.optionHighlight);
        return h ? h.optionHighlight : null;
      };
      // single-family sections (static title) show their product's info too
      const filter = this._catFilter[sec.id] || this.defaultCatFilter(sec, fams);
      let title = null, text = null, highlight = null;
      // Material buckets (Wood / Titanium / Leather) had no panel at all, so a
      // note authored on a wood sleeve had nowhere to appear. Read the bucket's
      // members the same way a family's are read.
      if (filter && String(filter).indexOf('mat:') === 0) {
        const members = this.sectionSingleBuckets(sec, fams).get(String(filter).slice(4));
        if (members && members.length) {
          text = infoOf(members);
          highlight = highlightOf(members);
          if (text || highlight) title = String(filter).slice(4);
        }
      } else if (filter && fams.has(filter)) {
        const f = fams.get(filter);
        text = infoOf(f.members);
        highlight = highlightOf(f.members);
        if (text || highlight) title = f.label;
      } else if (sec.id === '__acc' ) {
        const products = this.sectionProducts(sec);
        const key = filter || (products.size ? products.keys().next().value : null);
        if (key && products.has(key)) {
          const p = products.get(key);
          text = infoOf(p.members);
          highlight = highlightOf(p.members);
          if (text || highlight) title = p.label;
        }
      }
      // Fallback: a group whose options are all SEPARATE products makes no
      // families and no material buckets, so none of the branches above fire and
      // it got no panel at all — the heritage stems are exactly that (18 woods,
      // one product each). Read across the whole section instead, titled with the
      // group's own name. group_note would have been the obvious home for this
      // copy, but it is only rendered by three OTHER layout paths, never by
      // buildNavSectionRow, so a note set there is stored and silently unused.
      // 2026-09-01: this guard used to sit only on the TITLE, so a multi-group
      // section still took its TEXT from here. Accessories is ONE section holding
      // nine groups, so every category inherited whichever option happened to
      // carry the first option_info -- Torches showed the tip adapter's copy.
      // Reading across a whole section only makes sense when the section IS one
      // group, which is the case this fallback was written for.
      if (!text && !highlight && sec.groups.length === 1) {
        const all = [];
        sec.groups.forEach((g) => g.options.forEach((o) => all.push(o)));
        text = infoOf(all);
        highlight = highlightOf(all);
        if (text || highlight) {
          title = this.cleanLabel(sec.groups[0].summaryLabel || sec.groups[0].label);
        }
      }
      if (!text && !highlight) return null;
      const panel = document.createElement('div');
      panel.className = 'product-builder__catinfo';
      const h = document.createElement('div');
      h.className = 'product-builder__catinfo-title';
      h.textContent = title;
      panel.append(h);
      if (text) {
        const body = document.createElement('div');
        body.className = 'product-builder__catinfo-text';
        body.textContent = text;
        panel.append(body);
      }
      if (highlight) {
        const em = document.createElement('em');
        em.className = 'product-builder__catinfo-highlight';
        // No body copy means the panel is just a title and a one-line caveat
        // (the Wood buckets). Sit them on the SAME line rather than burning two
        // rows of a band that is already tight on a phone.
        if (!text) em.classList.add('product-builder__catinfo-highlight--inline');
        em.textContent = highlight;
        panel.append(em);
      }
      if (!text && highlight) panel.classList.add('product-builder__catinfo--tight');
      return panel;
    }

    buildSectionSwatchRow(sec, fams) {
      const rowEl = document.createElement('div');
      rowEl.className = 'product-builder__options product-builder__options--tray product-builder__swatchrow';
      rowEl.classList.add('product-builder__swatchrow--labeled');
      const isAcc = sec.id === '__acc';
      // one gift card per ROW, not per option: collect while building, append once
      const gifts = new Map();
      const flushGifts = () => {
        gifts.forEach((g) => {
          const gift = document.createElement('div');
          gift.className = 'product-builder__swatch-gift';
          const gname = this.cleanLabel(g.title || 'Free gift');
          gift.innerHTML = '<span class="product-builder__swatch-price product-builder__swatch-gift-with"></span>'
            + '<span class="product-builder__swatch-gift-thumb"><img src="' + g.img + '" alt="' + gname + '" loading="lazy"><span class="product-builder__swatch-gift-free">FREE</span></span>'
            + '<span class="product-builder__swatch-gift-label"></span>';
          // textContent, not innerHTML — the title is merchant-authored
          gift.querySelector('.product-builder__swatch-gift-label').textContent = gname;
          rowEl.appendChild(gift);
        });
        return rowEl;
      };
      // When every option in the row belongs to ONE product (the product IS the
      // category, e.g. Chamber or Clip), the product name is redundant on each
      // tile — show just what distinguishes them (Polished / Sandblasted).
      // Mixed rows keep their full names.
      const renderOpts = (list, groupOf) => {
        list = this.pairByProduct(list);
        const real = list.filter(Boolean);
        const handles = new Set(real.filter((o) => o.productHandle).map((o) => o.productHandle));
        const prefix = (handles.size === 1 && real.length > 1) ? this.familyLabel(real) : null;
        list.forEach((o) => addSwatch(groupOf(o), o, prefix));
      };
      const addSwatch = (group, option, prefix) => {
        const b = this.createOptionButton(group, option);
        if (option && prefix) {
          const lbl = b.querySelector('.product-builder__option-label');
          if (lbl) lbl.textContent = this.variantChipLabel(option, prefix);
        }
        // relative price above the swatch (blank keeps the row aligned):
        // included/base options show nothing, upcharges show +$X (Benchmade)
        const price = document.createElement('span');
        price.className = 'product-builder__swatch-price';
        const fmtDelta = (d) => (d > 0 ? '+' : d < 0 ? '\u2212' : '') + this.formatMoney(Math.abs(d)).replace(/\.00$/, '');
        if (option && !option.displayOnly && option.priceCents !== null) {
          const pp = this.priceParts(option, group);
          // One figure only, never a was/now pair. The discounted delta IS the
          // price difference the shopper pays, so the old price added a line of
          // noise to every accessory tile (all of them carry the 10%) without
          // telling them anything actionable. Colour does the work instead:
          // white = costs more, red = costs less. Removed.
          // A group carrying a CREDIT prices against the credit, not against a
          // base option: the $40 sleeve credit covers any sleeve up to $40, so
          // those read "Included" and dearer ones show only the difference paid.
          // Measuring off the first option produced negative numbers for every
          // sleeve cheaper than it, which is not what the shopper is charged.
          const groupCredit = Number(group && group.creditCents) || 0;
          // Feeds the SAME chain below rather than returning early — the gift
          // collection runs after the price block, and an early return would
          // silently skip it for any credit group that ever carries a gift.
          // max(0, …) is what kills the negatives: a $30 sleeve against a $40
          // credit is 0 -> "Included", never "−$10".
          const deltaNew = groupCredit > 0
            ? Math.max(0, pp.effective - groupCredit)
            : pp.effective - this.baseCents(group);
          if (deltaNew === 0) {
            // same value as the default option: it costs nothing extra, so say
            // so rather than leaving the slot blank
            price.textContent = 'Included';
            price.classList.add('product-builder__swatch-price--included');
          } else if (deltaNew < 0 && pp.effective > 0) {
            // CHEAPER than the default. This used to say "Included" too, which
            // hid a real saving — the $60 heritage stems come out $9 under the
            // walnut base and read as costing the same, then the total dropped
            // unexpectedly at checkout. Show the actual figure.
            // pp.effective > 0 guards the priceless blocks: Micro B&W Ebony has
            // no variant wired, so it prices at 0 and would claim the WHOLE base
            // off (−$63) instead of quietly reading "Included".
            price.textContent = fmtDelta(deltaNew);
            price.classList.add('product-builder__swatch-price--lower');
          } else {
            // upcharge: plain white, discounted or not
            price.textContent = fmtDelta(deltaNew);
          }
        }
        b.prepend(price);
        rowEl.appendChild(b);
        // buy-one-get-one display: remember the gift; rendered once per row.
        // ONLY for an option that's actually selected — the card is a statement
        // that you HAVE earned this, so showing it beside every torch in the row
        // read as three free stations rather than one you've qualified for.
        if (option && option.giftImage && !gifts.has(option.giftImage)
            && this.getSelectedIds(group).includes(option.id)) {
          gifts.set(option.giftImage, { img: option.giftImage, title: option.giftTitle });
        }
      };
      if (isAcc) {
        const products = this.sectionProducts(sec);
        const filter = this._catFilter[sec.id] || this.defaultCatFilter(sec, fams);
        if (filter === 'all') {
          // Every add-on in one row. No per-product "None" tile here — with
          // multi-select, clicking a chosen accessory already clears it, and a
          // None per product would be a wall of empty tiles.
          products.forEach((p) => renderOpts(p.members, () => p.group));
          return flushGifts();
        }
        if (filter && products.has(filter)) {
          const p = products.get(filter);
          addSwatch(p.group, null);
          renderOpts(p.members, () => p.group);
          return flushGifts();
        }
      }
      const filter = this._catFilter[sec.id] || this.defaultCatFilter(sec, fams);
      if (!isAcc && filter !== 'all' && fams.has(filter)) {
        const f = fams.get(filter);
        renderOpts(f.members, () => f.group);
        return flushGifts();
      }
      if (!isAcc && filter.indexOf('mat:') === 0) {
        const buckets = this.sectionSingleBuckets(sec, fams);
        const material = filter.slice(4);
        if (buckets.has(material)) {
          const byGroup = new Map();
          sec.groups.forEach((g) => g.options.forEach((o) => byGroup.set(o.id, g)));
          renderOpts(buckets.get(material), (o) => byGroup.get(o.id));
          return flushGifts();
        }
      }
      if (sec.groups.length === 1 && !sec.groups[0].required) {
        addSwatch(sec.groups[0], null);
      }
      const allOpts = [], ownerOf = new Map();
      sec.groups.forEach((g) => g.options.forEach((o) => {
        if (this.sectionPickable(o)) { allOpts.push(o); ownerOf.set(o.id, g); }
      }));
      renderOpts(allOpts, (o) => ownerOf.get(o.id));
      return flushGifts();
    }

    // Keep a product's finishes side by side. Authoring order interleaves some
    // of them — the ti sleeves run Argyle Polished, Topographic Polished, Argyle
    // Sandblasted, Topographic Sandblasted — so in a mixed row (ALL, or a
    // material bucket) the two halves of a pair end up separated by a different
    // product. Groups by product in FIRST-APPEARANCE order, so the row's overall
    // sequence is untouched and only the strays move up to join their partner.
    // Price order is deliberately not considered; pairing wins.
    pairByProduct(list) {
      const buckets = new Map();
      const order = [];
      list.forEach((o, i) => {
        // null entries are the "None" tile — keep each one unique so it holds
        // its place instead of collapsing into a product bucket
        const key = (o && o.productHandle) ? 'p:' + o.productHandle : 'x:' + i;
        if (!buckets.has(key)) { buckets.set(key, []); order.push(key); }
        buckets.get(key).push(o);
      });
      const out = [];
      order.forEach((k) => buckets.get(k).forEach((o) => out.push(o)));
      return out;
    }

    // Edge arrows for a scrollable swatch row (Benchmade's more-content cue).
    buildSwatchScroller(rowEl) {
      const wrap = document.createElement('div');
      wrap.className = 'product-builder__swatchwrap';
      wrap.appendChild(rowEl);
      const mk = (dir) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'product-builder__swatcharrow product-builder__swatcharrow--' + (dir > 0 ? 'right' : 'left');
        b.setAttribute('aria-label', dir > 0 ? 'More options' : 'Previous options');
        b.textContent = dir > 0 ? '\u2192' : '\u2190';
        b.addEventListener('click', () => {
          rowEl.scrollBy({ left: dir * rowEl.clientWidth * 0.7, behavior: 'smooth' });
        });
        return b;
      };
      const left = mk(-1), right = mk(1);
      const update = () => {
        const max = rowEl.scrollWidth - rowEl.clientWidth - 1;
        left.classList.toggle('is-hidden', rowEl.scrollLeft <= 1);
        right.classList.toggle('is-hidden', max <= 0 || rowEl.scrollLeft >= max);
        // Benchmade centers small option sets; rows that overflow stay
        // start-aligned so scrolling can reach every item
        rowEl.classList.toggle('is-centered', max <= 0);
      };
      rowEl.addEventListener('scroll', update, { passive: true });
      requestAnimationFrame(update);
      wrap.append(left, right);
      return wrap;
    }

    // Variant families of a group: options sharing a product (2+ with concrete
    // variants). Multi-select groups stay flat (no families).
    familiesForGroup(group) {
      if (group.allowMultiple) return new Map();
      const pickable = (o) => this.config.hideSoldOut ? (o.purchasable || o.displayOnly) : true;
      const byProduct = new Map();
      group.options.forEach((o) => {
        if (!o.productHandle || !o.variantId || !pickable(o)) return;
        if (!byProduct.has(o.productHandle)) byProduct.set(o.productHandle, []);
        byProduct.get(o.productHandle).push(o);
      });
      const fams = new Map();
      byProduct.forEach((members, handle) => { if (members.length >= 2) fams.set(handle, members); });
      return fams;
    }

    // Benchmade categorizes by MATERIAL (G10 / Aluminum / Titanium) — bucket
    // our options the same way from their names. Returns an ordered Map
    // material -> options. Buckets with no matches are dropped.
    materialOf(o) {
      const hay = ((o.productTitle || '') + ' ' + (o.label || '') + ' ' + (o.productHandle || '')).toLowerCase();
      if (/leather/.test(hay)) return 'Leather';
      // Species whose names contain no standalone "wood": blackwood fails
      // \bwood\b (no boundary after "black"), cocobolo has no "wood" at all.
      // A species missing here lands in the "More" bucket, which is unreachable
      // while the band opens on the first category — that hid Blackwood, Micro
      // Blackwood and Micro Cocobolo from the Heritage stems entirely.
      // Add new species HERE as they are stocked; do not pre-add speculatively.
      if (/purple heart|olivewood|olive wood|walnut|kingwood|pink ivory|bog oak|ebony|rosewood|blackwood|cocobolo|\bwood\b/.test(hay)) return 'Wood';
      if (/silicone/.test(hay)) return 'Silicone';
      if (/glass/.test(hay)) return 'Glass';
      if (/stainless/.test(hay)) return 'Stainless';
      if (/titanium|\bti\b/.test(hay)) return 'Titanium';
      return 'More';
    }

    // Material buckets of the options NOT already covered by a family — so
    // every sleeve (wood species, leathers...) lives in some category.
    sectionSingleBuckets(sec, fams) {
      const buckets = new Map();
      this.sectionSingles(sec, fams).forEach((o) => {
        const m = this.materialOf(o);
        if (!buckets.has(m)) buckets.set(m, []);
        buckets.get(m).push(o);
      });
      return buckets;
    }

    buildCategoryStrip(group, buckets) {
      const strip = document.createElement('div');
      strip.className = 'product-builder__catstrip';
      if (!buckets || !buckets.size) {
        strip.classList.add('product-builder__catstrip--empty');
        return strip;
      }
      // A single material still shows its title for context — just no "All"
      // chooser next to it.
      if (buckets.size === 1) {
        const label = document.createElement('span');
        label.className = 'product-builder__cat is-active product-builder__cat--static';
        label.textContent = buckets.keys().next().value;
        strip.appendChild(label);
        return strip;
      }
      const current = this._catFilter[group.id] || 'all';
      const mk = (key, label) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'product-builder__cat';
        if (key === current) b.classList.add('is-active');
        b.textContent = label;
        b.addEventListener('click', () => {
          this._catFilter[group.id] = key;
          this.renderControls();
        });
        return b;
      };
      strip.appendChild(mk('all', 'All'));
      buckets.forEach((opts, material) => strip.appendChild(mk(material, material)));
      return strip;
    }

    buildSwatchRow(group, buckets) {
      const rowEl = document.createElement('div');
      rowEl.className = 'product-builder__options product-builder__options--tray product-builder__swatchrow';
      const filter = this._catFilter[group.id] || 'all';
      const pickable = (o) => !(this.config.hideSoldOut && !o.purchasable && !o.displayOnly);
      const opts = (filter !== 'all' && buckets && buckets.has(filter))
        ? buckets.get(filter)
        : group.options.filter(pickable);
      if (!group.required) rowEl.appendChild(this.createOptionButton(group, null));
      opts.forEach((o) => rowEl.appendChild(this.createOptionButton(group, o)));
      return rowEl;
    }

    updateRow(group) {
      const row = this.controlsElement.querySelector('.product-builder__row[data-group-id="' + group.id + '"]');
      if (!row) return;
      const slider = row.querySelector('.product-builder__options--tray');
      if (slider) this.updateTraySelection(group, slider);
      this.updateRowPick(group);
    }

    updateRowPick(group) {
      const row = this.controlsElement.querySelector('.product-builder__row[data-group-id="' + group.id + '"]');
      if (!row) return;
      const pick = row.querySelector('.product-builder__row-pick');
      if (pick) this.fillPick(pick, group);
    }

    centerSlider(slider, instant) {
      if (!slider) return;
      const target = slider.querySelector('.product-builder__option.is-selected') || slider.firstElementChild;
      if (!target) return;
      const left = target.offsetLeft - (slider.clientWidth - target.offsetWidth) / 2;
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = (instant || reduce) ? 'auto' : 'smooth';
      slider.scrollTo({ left: Math.max(0, left), behavior: behavior });
    }

    nextUnfinishedGroupAfter(groupId) {
      const idx = this.groups.findIndex((g) => g.id === groupId);
      for (let i = idx + 1; i < this.groups.length; i++) {
        const g = this.groups[i];
        if (g.required && !this.getSelectedOptionForGroup(g)) return g.id;
      }
      return null;
    }

    // Fit the whole stage builder in the viewport (no page scroll): size the
    // section to the space below its top, then size the model to the largest
    // square that fits the flex area left over above the rail/tray/action bar.
    fitStage() {
      if (!this.stageMode) return;
      const el = this.section;
      if (getComputedStyle(el).display === 'none') return;

      el.style.height = 'auto';
      const top = el.getBoundingClientRect().top;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      el.style.height = Math.max(Math.round(vh - top), 520) + 'px';

      this.fitModel(); // no-op unless stage mode
    }

    // Size the model card to the largest square that fits the space the flex
    // layout currently gives it. Re-run when that space changes — viewport
    // resize, or switching to a component whose option cards are a different
    // height (e.g. discounted cards are taller) — so the model never overlaps
    // the tray below it.
    fitModel() {
      if (!this.stageMode) return;
      const el = this.section;
      if (getComputedStyle(el).display === 'none') return;
      const col = el.querySelector('.product-builder__preview-column');
      const card = el.querySelector('.product-builder__preview-card');
      if (!col || !card) return;
      const preview = card.querySelector('.product-builder__preview');
      card.style.width = '';
      card.style.height = '';
      const cs = getComputedStyle(col);
      const availW = col.clientWidth - parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0);
      const availH = col.clientHeight - parseFloat(cs.paddingTop || 0) - parseFloat(cs.paddingBottom || 0);

      if (this.modelHorizontal) {
        // Short, wide band. Height is a fraction of the viewport (so it reclaims
        // vertical space for the tray); the model art is a square, rotated 90°,
        // and the card clips its top/bottom to that band.
        const ratio = 2.4;
        const vh = window.innerHeight || document.documentElement.clientHeight || 800;
        let h = Math.round(vh * 0.26);
        let w = Math.min(availW, Math.round(h * ratio));
        h = Math.max(70, Math.round(w / ratio));
        card.style.width = w + 'px';
        card.style.height = h + 'px';
        if (preview) { preview.style.width = w + 'px'; preview.style.height = w + 'px'; }
        return;
      }

      const size = Math.max(80, Math.floor(Math.min(availW, availH)));
      card.style.width = size + 'px';
      card.style.height = size + 'px';
      if (preview) { preview.style.width = ''; preview.style.height = ''; }
    }

    // Resolve an option's displayed pricing. option.discountPriceCents:
    // null -> inherit the section-wide discount % (config.discountPct)
    // 0 -> no discount (show the regular price only)
    // > 0 -> that exact sale price
    // Returns { regular, effective, discounted } in cents.
    priceParts(option, group) {
      const base = Number(option.priceCents || 0);
      const dpo = option.discountPriceCents;

      // Explicit per-option sale price wins (0 = no discount on this option).
      if (dpo !== null && dpo !== undefined) {
        if (dpo === 0) return { regular: base, effective: base, discounted: false };
        return { regular: base, effective: Number(dpo), discounted: true };
      }
      // Else per-group discount % if set, otherwise the section-wide discount %.
      const pct = (group && group.discountPct != null) ? Number(group.discountPct) : Number(this.config.discountPct || 0);
      if (pct > 0) return { regular: base, effective: this.applyDiscount(base, pct), discounted: true };
      return { regular: base, effective: base, discounted: false };
    }

    // The "base" option a group's relative prices are measured against: the
    // default-selected option (or the first available for a required group;
    // optional groups have no base — their base price is 0 / "no selection").
    baseOptionForGroup(group) {
      const pickable = (o) => this.config.hideSoldOut ? (o.purchasable || o.displayOnly) : o.available;
      const def = group.options.find((o) => o.selectedByDefault && pickable(o));
      if (def) return def;
      if (group.required) return group.options.find((o) => pickable(o)) || null;
      return null;
    }

    baseCents(group) {
      const b = this.baseOptionForGroup(group);
      if (!b || b.priceCents == null) return 0;
      return this.priceParts(b, group).effective;
    }

    baseRegularCents(group) {
      const b = this.baseOptionForGroup(group);
      if (!b || b.priceCents == null) return 0;
      return this.priceParts(b, group).regular;
    }

    createOptionButton(group, option) {
      const isNone = option === null;
      const optionId = isNone ? '' : option.id;
      const isSelected = isNone
        ? this.getSelectedIds(group).length === 0
        : this.getSelectedIds(group).includes(option.id);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'product-builder__option';
      button.dataset.groupId = group.id;
      button.dataset.optionId = optionId;
      button.disabled = this.isSubmitting;

      if (!isNone && option.layerImage) {
        button.dataset.layerImage = option.layerImage;

        button.addEventListener('mouseenter', () => {
          this.preloadImage(option.layerImage).catch(() => {});
        });

        button.addEventListener('focus', () => {
          this.preloadImage(option.layerImage).catch(() => {});
        });

        button.addEventListener('touchstart', () => {
          this.preloadImage(option.layerImage).catch(() => {});
        }, { passive: true });
      }

      if (isSelected) {
        button.classList.add('is-selected');
      }

      if (!isNone && !option.purchasable && !option.displayOnly) {
        button.classList.add('is-sold-out');
      }

      // Stage tray / rows slider: a variant-image thumbnail on top of each card
      // (falls back to the preview layer image, then an empty placeholder).
      if (this.stageMode || this.optionRows || this.cardThumbs) {
        const thumb = document.createElement('span');
        thumb.className = 'product-builder__option-thumb';
        const thumbSrc = isNone ? null : (option.optionImage || option.layerImage);
        if (thumbSrc) {
          const im = document.createElement('img');
          im.src = thumbSrc;
          im.alt = isNone ? '' : option.label;
          im.loading = 'lazy';
          thumb.appendChild(im);
        } else {
          thumb.classList.add('product-builder__option-thumb--empty');
          thumb.textContent = isNone ? 'None' : '';
        }
        button.appendChild(thumb);
      }

      const label = document.createElement('span');
      label.className = 'product-builder__option-label';
      label.textContent = isNone ? 'No Selection' : option.label;

      const detail = document.createElement('span');
      detail.className = 'product-builder__option-detail';

      const pp = (isNone || option.priceCents === null) ? null : this.priceParts(option, group);

      if (isNone) {
        detail.textContent = 'No selection';
      } else if (option.priceCents === null) {
        detail.textContent = 'Unavailable';
      } else if (!option.purchasable && !option.displayOnly) {
        if (pp.discounted) {
          detail.textContent = `${this.formatMoney(pp.effective)} · Sold out`;
        } else {
          detail.textContent = `${this.formatMoney(pp.effective)} · Sold out`;
        }
      } else if (this.relativePricing) {
        // Show the difference from this group's default option, not the sticker price.
        if (option.displayOnly || option.priceCents === 0) {
          detail.textContent = 'Included';
        } else {
          const delta = pp.effective - this.baseCents(group);
          if (delta === 0) detail.textContent = 'Included';
          else if (delta > 0) detail.textContent = '+' + this.formatMoney(delta);
          else detail.textContent = '−' + this.formatMoney(-delta);
        }
      } else {
        if (option.priceCents === 0) {
          detail.textContent = 'Free';
        } else if (pp.discounted) {
          detail.textContent = this.formatMoney(pp.effective);
        } else {
          detail.textContent = this.formatMoney(pp.effective);
        }
      }

      button.appendChild(label);

      if (!isNone && option.subtitle) {
        const subtitle = document.createElement('span');
        subtitle.className = 'product-builder__option-subtitle';
        subtitle.textContent = option.subtitle;
        button.appendChild(subtitle);
      }

      button.appendChild(detail);

      button.addEventListener('click', async () => {
        if (this.isSubmitting) return;

        if (isNone) {
          this.selectedByGroup[group.id] = [];
        } else if (group.allowMultiple) {
          const ids = this.getSelectedIds(group).slice();
          const idx = ids.indexOf(option.id);
          if (idx !== -1) {
            if (!(group.required && ids.length === 1)) ids.splice(idx, 1);
          } else {
            const max = group.maxSelections > 0 ? group.maxSelections : Infinity;
            if (ids.length < max) ids.push(option.id);
          }
          this.selectedByGroup[group.id] = ids;
          this.applyLinkedVariant(option);
        } else if (!group.required && this.getSelectedIds(group)[0] === option.id) {
          // optional single-select: tapping the selected option deselects it
          this.selectedByGroup[group.id] = [];
        } else {
          this.selectedByGroup[group.id] = [option.id];
          this.applyLinkedVariant(option);
        }

        button.classList.add('is-loading');

        try {
          await this.preloadCurrentSelection();
        } catch (error) {
          console.warn(error);
        } finally {
          button.classList.remove('is-loading');
        }

        this.clearMessages();
        this.render();
        const slider = button.closest('.product-builder__options--tray');
        if (slider) requestAnimationFrame(() => this.centerSlider(slider, false));
      });

      return button;
    }

    // Accessories are single-select per category, so they get no +/- stepper.
    // The stepper stays for required groups that opt in via the theme setting
    // (e.g. multiple sleeves), where quantity is the point.
    showsMultiControl(group) {
      return Boolean(group && group.allowMultiple && group.required);
    }

    createMultiControl(group) {
      const wrap = document.createElement('div');
      wrap.className = 'product-builder__multi-ctl';
      const ids = this.getSelectedIds(group);
      const max = group.maxSelections > 0 ? group.maxSelections : group.options.length;
      const minCount = group.required ? 1 : 0;

      if (ids.length > minCount) {
        const minus = document.createElement('button');
        minus.type = 'button';
        minus.className = 'product-builder__multi-btn';
        minus.textContent = '\u2212';
        minus.setAttribute('aria-label', 'Remove one ' + group.label);
        minus.disabled = this.isSubmitting;
        minus.addEventListener('click', () => {
          const a = this.getSelectedIds(group).slice();
          if (a.length > minCount) { a.pop(); this.selectedByGroup[group.id] = a; this.clearMessages(); this.render(); }
        });
        wrap.appendChild(minus);
      }

      const count = document.createElement('span');
      count.className = 'product-builder__multi-count';
      count.textContent = String(ids.length);
      wrap.appendChild(count);

      const plus = document.createElement('button');
      plus.type = 'button';
      plus.className = 'product-builder__multi-btn';
      plus.textContent = '+';
      plus.setAttribute('aria-label', 'Add another ' + group.label);
      plus.disabled = this.isSubmitting || ids.length >= max;
      plus.addEventListener('click', () => {
        const a = this.getSelectedIds(group).slice();
        const next = group.options.find((o) => !a.includes(o.id));
        if (next && a.length < max) { a.push(next.id); this.selectedByGroup[group.id] = a; this.clearMessages(); this.render(); }
      });
      wrap.appendChild(plus);

      return wrap;
    }

    async handleRandomize() {
      if (this.isSubmitting) return;

      if (this.randomizeButton) {
        this.randomizeButton.disabled = true;
      }

      this.groups.forEach((group) => {
        // Only randomize among options a customer could actually pick — skip
        // sold-out / unavailable ones (which may also be hidden), so the result
        // is always a valid, addable build.
        const selectable = group.options.filter((o) => o.purchasable || o.displayOnly);
        const pool = group.required ? selectable : [null, ...selectable];

        if (!pool.length) return;

        const randomPick = pool[Math.floor(Math.random() * pool.length)];

        if (randomPick === null) {
          delete this.selectedByGroup[group.id];
        } else {
          this.selectedByGroup[group.id] = randomPick.id;
          this.applyLinkedVariant(randomPick);
        }
      });

      try {
        await this.preloadCurrentSelection();
      } catch (error) {
        console.warn(error);
      } finally {
        if (this.randomizeButton) {
          this.randomizeButton.disabled = false;
        }
      }

      this.clearMessages();
      this.render();

      // Rows/coverflow: JUMP each slider straight to its new pick (instant, not a
      // smooth scroll that animates through every option and re-fires the
      // scroll-select handler along the way). Suppress scroll-select during the jump.
      this._skipScrollSelect = true;
      this.controlsElement.querySelectorAll('.product-builder__options--tray').forEach((slider) => {
        this.centerSlider(slider, true);
      });
      window.setTimeout(() => { this._skipScrollSelect = false; }, 100);
    }

    startRandomizeLoop() {
      this.randomizeInterval = setInterval(() => {
        this.handleRandomize();
      }, this.randomizePaceMs || 1000);
    }

    stopRandomizeLoop() {
      clearInterval(this.randomizeInterval);
      this.randomizeInterval = null;
    }

    renderSummary() {
      this.summaryElement.innerHTML = '';

      if (!this.groups.length) {
        const empty = document.createElement('p');
        empty.className = 'product-builder__summary-empty';
        empty.textContent = 'Add builder option blocks to this section.';
        this.summaryElement.appendChild(empty);
        return;
      }

      // A torch and an induction heater both earn the station, but only ONE per
      // kit — and both groups can be selected at once, so the row is latched.
      let stationShown = false;

      this.groups.forEach((group) => {
        const selectedOpts = this.getSelectedOptionsForGroup(group);

        const row = document.createElement('div');
        row.className = 'product-builder__summary-row';

        const label = document.createElement('span');
        label.textContent = this.cleanLabel(group.summaryLabel || group.label);

        const value = document.createElement('strong');

        if (!selectedOpts.length) {
          value.textContent = group.required ? 'Required' : 'None';
        } else {
          value.textContent = selectedOpts
            .map((o) => (!o.purchasable && !o.displayOnly) ? `${o.label} (Sold out)` : o.label)
            .join(', ');
        }

        row.appendChild(label);
        row.appendChild(value);

        this.summaryElement.appendChild(row);

        // Freebies shown in the summary (informational — cart-reconciler.liquid
        // adds the real $0 lines). These MUST track the reconciler's config:
        // free WPA handle -> handleGiftFamilies, hydro only, follows the chamber
        // free station -> giftFamilies (ti/heritage/hydro) + any trigger variant
        // The station widened to every build and gained the induction heater on
        //; this block stayed hydro-and-torch-only, so Ti/Heritage buyers
        // and anyone picking a heater were promised nothing while the cart still
        // gave them a station.
        {
          const build = this.section.getAttribute('data-build');
          const gl = group.label.toLowerCase();
          var freebie = null;
          if (build === 'hydro' && this.getKitRole(group.label) === 'chamber') {
            freebie = 'Free WPA Handle';
          } else if (!stationShown && selectedOpts.length
                     && (gl.indexOf('torch') !== -1 || gl.indexOf('induction') !== -1
                         || gl.indexOf('heater') !== -1)) {
            freebie = 'Free Cooling Station';
            stationShown = true;
          }
          if (freebie) {
            var freeRow = document.createElement('div');
            freeRow.className = 'product-builder__summary-row';
            var fLabel = document.createElement('span');
            fLabel.textContent = freebie;
            var fValue = document.createElement('strong');
            fValue.textContent = 'FREE';
            fValue.style.color = '#c00000';
            freeRow.appendChild(fLabel);
            freeRow.appendChild(fValue);
            this.summaryElement.appendChild(freeRow);
          }
        }
      });

      // const coolingRow = document.createElement('div');
      // coolingRow.className = 'product-builder__summary-row';

      // const coolingValue = document.createElement('strong');
      // coolingValue.style.color = '#e53e3e';
      // coolingValue.innerHTML = '+ Free <a href="https://simrellcollection.com/products/cooling-station" target="_blank" rel="noopener" style="color: #e53e3e; text-decoration: underline;">Cooling Station</a>';

      // coolingRow.appendChild(coolingValue);
      // this.summaryElement.appendChild(coolingRow);
    }

    // A group's build credit: the cart takes a fixed amount off ONE line in
    // this group (the free-sleeve-with-a-build rule), so the quoted price has
    // to lose the same amount or the buy bar reads higher than checkout.
    // Capped at what that option actually costs — never below zero.
    groupCreditCents(group) {
      const credit = Number(group && group.creditCents) || 0;
      if (credit <= 0) return 0;
      const picked = this.getSelectedOptionsForGroup(group)[0];
      if (!picked) return 0;
      return Math.min(credit, this.priceParts(picked, group).effective);
    }

    // Both the summary total and the BUY NOW label price the build; they used
    // to sum it separately and could drift apart.
    // Retail value of the freebies this build has EARNED. Dedup is by gift
    // image, matching the gift card and the rail chip — a torch and an
    // induction heater in one build earn ONE station, not two.
    earnedGiftCents() {
      const seen = new Set();
      let cents = 0;
      this.groups.forEach((g) => {
        this.getSelectedOptionsForGroup(g).forEach((o) => {
          if (!o.giftImage || seen.has(o.giftImage)) return;
          seen.add(o.giftImage);
          cents += Number(o.giftPriceCents) || 0;
        });
      });
      return cents;
    }

    buildTotals() {
      let effective = 0;
      let regular = 0;
      this.getSelectedOptions().forEach((item) => {
        const pp = this.priceParts(item.option, item.group);
        effective += pp.effective;
        regular += pp.regular;
      });
      let credit = 0;
      this.groups.forEach((g) => { credit += this.groupCreditCents(g); });
      // Earned freebies inflate the REGULAR side only. Nothing is charged for
      // them, so the strikethrough and "You save" both grow by their retail
      // value while the amount due is untouched.
      const gifts = this.earnedGiftCents();
      return { effective: Math.max(0, effective - credit), regular: regular + gifts, credit: credit, gifts: gifts };
    }

    renderTotal() {
      const selectedOptions = this.getSelectedOptions();
      const { effective: effectiveTotal, regular: regularTotal } = this.buildTotals();

      if (selectedOptions.length > 0 && effectiveTotal < regularTotal) {
        // "You save $X" rides in the TOTAL ROW rather than under BUY NOW. That
        // row already exists at every breakpoint, so the message costs no new
        // vertical space and lands where the shopper is already reading a
        // number — the mobile problem is avoided rather than solved.
        this.totalElement.innerHTML =
          `<s class="product-builder__total-original">${this.formatMoney(regularTotal)}</s>`
          + ` <span class="product-builder__total-discounted">${this.formatMoney(effectiveTotal)}</span>`
          + `<span class="product-builder__total-save">You save ${this.formatMoney(regularTotal - effectiveTotal)}</span>`;
        this.forceRepaint(this.totalElement);
      } else {
        this.totalElement.textContent = this.formatMoney(effectiveTotal);
      }
    }

    updateAddButton() {
      this.addButton.disabled = this.isSubmitting || !this.groups.length;

      if (this.randomizeButton) {
        this.randomizeButton.disabled = this.isSubmitting || !this.groups.length;
        if (this.ctabsActive()) {
          // Benchmade's three-dot slot: the menu button lives beside BUY NOW;
          // the real randomize button hides (the menu item drives it)
          if (!this.randomizeButton.classList.contains('product-builder__randomize-button--icon')) {
            this.randomizeButton.classList.add('product-builder__randomize-button--icon');
            this.randomizeButton.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 3h5v5"/><path d="M4 20L21 3"/><path d="M21 16v5h-5"/><path d="M15 15l6 6"/><path d="M4 4l5 5"/></svg>';
            this.randomizeButton.setAttribute('aria-label', 'Randomize build');
          }
          this.ensureMoreMenu();
        } else if (this.randomizeButton.classList.contains('product-builder__randomize-button--icon')) {
          this.randomizeButton.textContent = 'Randomize';
          this.randomizeButton.classList.remove('product-builder__randomize-button--icon');
        }
      }

      if (this.isSubmitting) {
        this.addButton.textContent = 'Adding...';
      } else if (this.ctabsActive()) {
        // Benchmade-style CTA: the price lives inside the button ("Buy Now — $X").
        const sel = this.getSelectedOptions();
        const { effective: eff, regular: reg } = this.buildTotals();
        const price = (sel.length && eff < reg)
          ? '<s class="product-builder__buy-strike">' + this.formatMoney(reg) + '</s>'
            + '<span class="product-builder__buy-now">' + this.formatMoney(eff) + '</span>'
          : '<span class="product-builder__buy-now">' + this.formatMoney(eff) + '</span>';
        // "You save" is a second line INSIDE the button, both breakpoints.
        // Sibling placements were tried twice and both failed: the buy button
        // lives in the corner cluster, so anything beside it is bounded by the
        // BAR, not the button — it drew across the component tabs on desktop and
        // overlapped the price on mobile. Inside the button it cannot collide.
        const saved = sel.length ? reg - eff : 0;
        this.addButton.innerHTML = 'Buy Now - ' + price
          + (saved > 0 ? '<span class="product-builder__buy-save">You save ' + this.formatMoney(saved) + '</span>' : '');
        this.forceRepaint(this.addButton);
        // the price just changed the cluster's width; re-reserve it
        requestAnimationFrame(() => this.publishClusterWidth());
      } else {
        // Shorter CTA on the dark thumbs theme (desktop + mobile).
        this.addButton.textContent = this.cardThumbs ? 'Add to cart' : (this.config.addButtonText || 'Add build to cart');
      }
    }

    getSelectedOptionForGroup(group) {
      return this.getSelectedOptionsForGroup(group)[0] || null;
    }

    getSelectedOptions() {
      const out = [];
      this.groups.forEach((group) => {
        this.getSelectedOptionsForGroup(group).forEach((option, slotIndex) => {
          out.push({ group, option, slotIndex });
        });
      });
      return out;
    }

    validateSelections() {
      const missingGroups = this.groups.filter((group) => {
        return group.required && !this.getSelectedOptionForGroup(group);
      });

      if (missingGroups.length) {
        return {
          valid: false,
          message: `Please select: ${missingGroups.map((group) => group.label).join(', ')}.`
        };
      }

      const invalidVariantGroups = this.getSelectedOptions().filter(({ option }) => {
        return !option.displayOnly && !option.variantId;
      });

      if (invalidVariantGroups.length) {
        return {
          valid: false,
          message: 'One or more selected options is unavailable.'
        };
      }

      const unavailableGroups = this.getSelectedOptions().filter(({ option }) => {
        return !option.displayOnly && !option.purchasable;
      });

      if (unavailableGroups.length) {
        return {
          valid: false,
          message: 'One or more selected options is currently sold out. Please update your build before adding to cart.'
        };
      }

      return {
        valid: true,
        message: ''
      };
    }

    // Map a builder group to a coarse kit role for cart grouping/labels.
    // "hydro" -> "Hydro", "assembly" -> "Assembly". Only ever applied to the
    // build key and the role, both of which are single lowercase words.
    titleCase(s) {
      s = String(s || '');
      return s.charAt(0).toUpperCase() + s.slice(1);
    }

    getKitRole(label) {
      const l = (label || '').toLowerCase();
      if (l.indexOf('sleeve') !== -1) return 'sleeve';
      if (l.indexOf('chamber') !== -1) return 'chamber';
      if (l.indexOf('stinger') !== -1) return 'stinger';
      if (l.indexOf('thermal') !== -1 || l.indexOf('assembly') !== -1) return 'assembly';
      // The 8mm adapters must be matched BEFORE the body rule below: that rule
      // maps 'adapter' -> body because the WPA (water piece adapter) IS the
      // hydro's body, which would otherwise stamp both optional adapters as
      // "Build: Ti \u00b7 Body" in the cart.
      if (l.indexOf('tip adapter') !== -1) return 'tip adapter';
      if (l.indexOf('stem adapter') !== -1) return 'stem adapter';
      if (l.indexOf('body') !== -1 || l.indexOf('adapter') !== -1 || l.indexOf('wpa') !== -1) return 'body';
      return 'accessory';
    }

    async handleAddToCart() {
      if (this.isSubmitting) return;

      this.clearMessages();

      const validation = this.validateSelections();
      if (!validation.valid) {
        this.showError(validation.message);
        return;
      }

      const selectedOptions = this.getSelectedOptions();
      if (!selectedOptions.length) {
        this.showError('Please select at least one option.');
        return;
      }

      this.isSubmitting = true;
      this.updateAddButton();

      const cartBound = selectedOptions.filter(({ option }) => !option.displayOnly);

      // Kit label "<family> <N>" (e.g. "hydro 1"): stamped as _build_<role> on every
      // line so fulfillment can assemble kits and the cart can group them. Family
      // comes from the section's Build key setting (data-build attribute); N = next
      // number for that family based on kits already in the cart.
      const family = this.section.getAttribute('data-build') || 'build';
      // Heritage comes in two body sizes and fulfillment needs the size on EVERY
      // line, not just the body's own title: a Micro body relabels the whole kit
      // "Micro Heritage". The prefix is also the grouping key in both carts, so
      // the kit count below runs against the DISPLAY label — a Micro and a
      // Classic Heritage in one cart stay two kits, and two Micros still number.
      let familyDisplay = this.titleCase(family);
      if (family.toLowerCase() === 'heritage') {
        const microBody = cartBound.some(({ group, option }) =>
          this.getKitRole(group.label) === 'body' && /\bmicro\b/i.test(option.label || ''));
        if (microBody) familyDisplay = 'Micro ' + familyDisplay;
      }
      let kitNumber = 1;
      try {
        const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cartData = await cartRes.json();
        // 'i' flag + splitting on the separator so BOTH shapes count:
        // old _build_chamber: "hydro 1"
        // new Build: "Hydro 1 · Chamber"
        // The number is OPTIONAL in the pattern because the first build stopped
        // carrying one on. A bare "Hydro" MUST still count as kit 1 —
        // miss that and a second build reuses 1, silently merging two kits into
        // one card. Old "Hydro 1" carts keep matching via the numbered branch.
        const numRe = new RegExp('^' + familyDisplay + '(?: (\\d+))?$', 'i');
        (cartData.items || []).forEach((item) => {
          Object.keys(item.properties || {}).forEach((key) => {
            if (key !== 'Build' && key.indexOf('_build_') !== 0) return;
            const raw = String(item.properties[key] || '').split('\u00b7')[0].trim();
            const m = numRe.exec(raw);
            if (m) kitNumber = Math.max(kitNumber, (m[1] ? parseInt(m[1], 10) : 1) + 1);
          });
        });
      } catch (e) { /* cart unreadable -> first kit */ }
      // The FIRST build is unnumbered — "Heritage · Chamber" reads better on a
      // packing slip and in the cart than "Heritage 1 · Chamber". Numbering
      // starts at 2 and only appears when a cart really holds two of a build,
      // which is what keeps the mini-cart grouping them as separate kits.
      // (`kitLabel` used to sit here; it was dead and is gone.)
      const kitDisplay = kitNumber === 1
        ? familyDisplay
        : `${familyDisplay} ${kitNumber}`;

      const WALNUT_SUBSTITUTIONS = new Map([
        [54112392610005, 53071397060821],
        [54112392642773, 53071397093589]
      ]);

      const walnutSelected = selectedOptions.some(({ option }) => option.displayOnly);

      const cartItems = cartBound
        .slice()
        .reverse()
        .map(({ group, option }) => {
          return {
            id: walnutSelected
              ? (WALNUT_SUBSTITUTIONS.get(option.variantId) || option.variantId)
              : option.variantId,
            quantity: 1,
            // VISIBLE property so fulfillment sees it on the packing slip:
            // Build: "Hydro 1 · Chamber"
            // One key for every line, so the shared "Hydro 1" is what groups a
            // build; the part name rides in the value. Replaced the hidden
            // _build_<role> form on — a leading underscore makes a
            // property hidden in Shopify, which kept it out of ShipStation.
            properties: { Build: kitDisplay + ' \u00b7 ' + this.titleCase(this.getKitRole(group.label)) }
          };
        });

      if (!cartItems.length) {
        this.showSuccess(this.config.successMessage || 'Build added to cart.');
        this.isSubmitting = false;
        this.updateAddButton();
        return;
      }

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items: cartItems })
        });

        if (!response.ok) {
          throw new Error('Could not add to cart');
        }

        const result = await response.json();

        this.showSuccess('Build added to cart');

        this.openCartDrawer(result);

      } catch (error) {
        console.error(error);
        this.showError('Error adding build to cart');
      } finally {
        this.isSubmitting = false;
        this.updateAddButton();
      }
    }

    getCartAddUrl() {
      const rootUrl = this.config.rootUrl || '/';
      return `${rootUrl.replace(/\/$/, '')}/cart/add.js`;
    }

    dispatchCartEvent(result, builderId) {
      document.dispatchEvent(
        new CustomEvent('product-builder:added', {
          bubbles: true,
          detail: {
            builderId,
            result
          }
        })
      );
    }

    applyDiscount(priceCents, pct) {
      return Math.round(priceCents * (1 - pct / 100));
    }

    // iOS Safari does not reliably repaint text that changes inside
    // .pb-container: that element is container-type:inline-size (a containment
    // context) and in frame mode the section above it is position:fixed. The
    // DOM updates and the pixels do not.
    // Confirmed on device 2026-09-01: the buy-now element READ $332.00 while
    // the screen SHOWED $305.00 -- the previous value, one update behind. The
    // struck price beside it repainted correctly, which is why this read as
    // "only the crossed-out price updates".
    // Toggling display and reading a layout property in ONE synchronous block
    // forces re-layout and repaint. The browser never paints the intermediate
    // state, so there is no flicker and no layout shift.
    forceRepaint(el) {
      if (!el) return;
      const prev = el.style.display;
      el.style.display = 'none';
      void el.offsetHeight;
      el.style.display = prev;
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;

      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.config.currency || 'USD'
        }).format(amount);
      } catch (error) {
        return `$${amount.toFixed(2)}`;
      }
    }

    clearMessages() {
      this.errorElement.hidden = true;
      this.errorElement.textContent = '';
      this.successElement.hidden = true;
      this.successElement.textContent = '';
    }

    openCartDrawer() {
      // The builder adds the build through its OWN /cart/add.js path, so Focal
      // never emits its native "cart:updated" for it. We fetch the full cart and
      // emit cart:updated ourselves — this is how the cart-guard snippet (which
      // listens for cart:updated) learns to add the free gift + insurance, and it
      // keeps other cart:updated listeners (free-shipping bar, etc.) correct too.
      // Then cart:refresh re-renders the drawer. (No global fetch wrapping — this
      // explicit signal replaces it, which is what stopped the request storms.)
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:updated', { bubbles: true, detail: { cart } })
          );
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } })
          );
        })
        .catch(() => {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        });

      requestAnimationFrame(() => {
        const cart = document.getElementById('mini-cart');
        if (cart) cart.open = true;
      });
    }

    showError(message) {
      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
    }

    showSuccess(message) {
      this.successElement.textContent = message;
      this.successElement.hidden = false;
      // The frame keeps the buy bar on screen for the whole session, so a
      // message that only clears on the next selection change can sit there
      // indefinitely. Time it out instead.
      clearTimeout(this._successTimer);
      this._successTimer = setTimeout(() => {
        this.successElement.hidden = true;
        this.successElement.textContent = '';
      }, 4000);
    }
  }

  function initProductBuilders() {
    var attract = /[?&]attract=1(?:&|$)/.test(window.location.search);
    document.querySelectorAll('[data-product-builder-section]').forEach((section) => {
      if (section.dataset.productBuilderInitialized === 'true') return;

      if (section.dataset.productBuilderDefer === 'true' && !attract) {
        return;
      }

      if (section.dataset.productBuilderLazy === 'true' && !attract) {
        setupLazyBuilder(section);
        return;
      }

      section.dataset.productBuilderInitialized = 'true';
      var builder = new ProductBuilder(section);
      if (attract) startAttract(section, builder);
    });
  }

  function setupLazyBuilder(section) {
    if (section.dataset.lazyWired === 'true') return;
    section.dataset.lazyWired = 'true';
    var trigger = section.querySelector('[data-builder-lazy-trigger]');
    if (!trigger) return;
    trigger.addEventListener('click', function () {
      if (section.dataset.productBuilderInitialized === 'true') return;
      section.dataset.productBuilderInitialized = 'true';
      new ProductBuilder(section);
      section.dataset.lazyState = 'revealed';
      var video = section.querySelector('.product-builder__lazy-video');
      if (video) { try { video.pause(); } catch (e) {} }
    }, { once: true });
  }

  // Attract / demo-reel mode, for filming the builder:
  // ?attract=1 randomize the visible build every second
  // ?attract=1&cycle=4...and jump to the next build every 4s
  // ?attract=1&pace=700...changing options every 700ms
  // Any tap stops it, so a stray touch doesn't ruin a take.
  function startAttract(section, builder) {
    if (!builder || typeof builder.startRandomizeLoop !== 'function') return;
    var q = new URLSearchParams(window.location.search);
    var every = parseInt(q.get('cycle') || '', 10);

    function stopAll() {
      if (window.__pbAttractCycle) { clearInterval(window.__pbAttractCycle); window.__pbAttractCycle = null; }
      document.querySelectorAll('[data-product-builder-section]').forEach(function (s) {
        if (s.__builder && typeof s.__builder.stopRandomizeLoop === 'function') s.__builder.stopRandomizeLoop();
      });
    }
    section.addEventListener('pointerdown', stopAll, { once: true });

    // --- cycling: ONE beat for the whole page ---------------------------
    // Switch the build and reroll it in the SAME tick. Letting each build run
    // its own randomize timer alongside a separate switch timer meant four
    // clocks with no common phase, so changes landed off-beat and it read as
    // jumpy. One clock, one visible change per beat.
    if (every > 0) {
      if (window.__pbAttractCycle) return;          // another section already drives it
      var beat = function () {
        // The INLINE section (product.titanium-with-builder) has no build
        // switcher and nothing sets.is-active-build, so both the switch and
        // the reroll have to degrade: with no tabs, just reroll this section.
        var tabs = [].slice.call(document.querySelectorAll('[data-build-switcher] [data-build-target]'));
        if (tabs.length > 1) {
          var active = document.querySelector('[data-product-builder-section].is-active-build');
          var key = active && active.getAttribute('data-build');
          var i = -1;
          tabs.forEach(function (t, n) { if (t.getAttribute('data-build-target') === key) i = n; });
          tabs[(i + 1) % tabs.length].click();
        }
        // reroll AFTER any switch has painted, so the new build is what changes
        requestAnimationFrame(function () {
          var now = document.querySelector('[data-product-builder-section].is-active-build') || section;
          if (now && now.__builder && typeof now.__builder.handleRandomize === 'function') {
            try { now.__builder.handleRandomize(); } catch (e) {}
          }
        });
      };
      window.__pbAttractCycle = setInterval(beat, Math.max(600, every * 1000));
      return;
    }

    // --- no cycling: randomize this build in place ----------------------
    var pace = parseInt(q.get('pace') || '', 10);
    builder.randomizePaceMs = (pace > 0) ? Math.max(200, pace) : 1000;
    try { builder.startRandomizeLoop(); } catch (e) {}
  }

  window.SimrellBuilder = window.SimrellBuilder || {};
  window.SimrellBuilder.initSection = function (section) {
    // Re-activating an already-built stage section: refit to the current viewport.
    if (section && section.__builder && section.__builder.stageMode) {
      requestAnimationFrame(() => section.__builder.fitStage());
    }
    // Each build's BUY label carries its own price, so the corner cluster is a
    // different width per build. Switching builds resizes NOTHING (the outgoing
    // section keeps its box), so the ResizeObserver can't see it — the switch
    // has to say so itself, or the tab bar keeps the previous build's reserve.
    if (section && section.__builder && section.__builder.publishClusterWidth) {
      requestAnimationFrame(() => section.__builder.publishClusterWidth());
    }
    if (!section || section.dataset.productBuilderInitialized === 'true') return null;
    section.dataset.productBuilderInitialized = 'true';
    var attract = /[?&]attract=1(?:&|$)/.test(window.location.search);
    var builder = new ProductBuilder(section);
    if (attract) startAttract(section, builder);
    return builder;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductBuilders);
  } else {
    initProductBuilders();
  }

  document.addEventListener('shopify:section:load', initProductBuilders);
})();